// ─── CHYRON + SQUAWK BOX ──────────────────────────────────────────────────────
// A full-width scrolling news ticker with an optional spoken readout.
// Self-contained: takes NO props and reads its own settings from localStorage,
// so it can be dropped anywhere in the tree without threading state.
//
// Data: a single call to /api/news/chyron, refetched every 5 minutes into a
// pool of up to 50. That endpoint serves curated RSS plus Finnhub's merger wire
// — no Currents — already paywall-filtered, feed-identity-checked, per-source
// floored, deduped and sorted newest-first. Items arrive as
//   { id, title, source, sourceApi, url, publishedAt, ageLabel, summary }
// where `id` is a hash of the url — so deduping on `id` IS deduping on url.
//
// The ticker shows a CAROUSEL: one batch of 15 at a time, advancing every 360s,
// so the whole pool is seen over four passes rather than only ever the newest
// 15. Keyword hits are injected from the WHOLE pool on every batch, never
// batch-scoped — an alert that waits four passes to appear is not an alert.
// ⚠ No sorting, no source floor and no recency cut happen here; the endpoint
// owns all three. The pool is served in backend order.
//
// SQUAWK READOUT: on top of the headlines, the box reads a periodic market
// summary assembled from live platform data — market / dark pool / unusual
// options / convergence / flights / news. It is a SEPARATE timer on a SEPARATE
// cadence (3 min in-session, 10 min extended, silent when closed, re-derived from
// the ET clock every cycle) and it shares only the voice queue, so one pump paces
// both and they can never talk over each other. See buildSquawkScript below.
//
// ⚠⚠ MOBILE TTS — THREE SILENT FAILURES, ALL HANDLED HERE, NONE OBSERVABLE.
// Android Chrome truncates utterances beyond ~200 characters mid-word and then
// silently drops every subsequent speak() call. iOS Safari truncates at ~220 and
// suspends synthesis entirely when the tab is backgrounded or the screen locks —
// leaving speechSynthesis.speaking TRUE with no audio and no onend, which
// deadlocks the pump. Neither throws, neither sets a flag, and both look exactly
// like a quiet feed. The defences: _chunkText caps every utterance at 180
// characters (under both limits), VOICE_GAP_MS widens to 900ms on mobile because
// both engines drop speak() calls that arrive too soon after the previous onend,
// and a visibilitychange handler cancels cleanly on hide and resumes on return
// rather than waiting out a deadlock. ⚠ iOS additionally IGNORES
// utterance.volume — system volume controls speech there, so the volume slider
// is Android and desktop only.
//
// ⚠⚠ BROWSER AUTOPLAY POLICY — NOT A BUG, AND THERE IS NO RELIABLE WORKAROUND.
// Browser autoplay policy requires a user gesture before AudioContext and
// speechSynthesis will work. hasGesture ref tracks whether a gesture has
// occurred. Audio is silently blocked until then — this is correct behavior.
//
// Two consequences the implementation has to respect:
//   1. The gate is DOCUMENT-WIDE, not per-component. One click anywhere unlocks
//      audio for the whole page, so the ref is fed from a document-level
//      listener (plus navigator.userActivation for a gesture that landed before
//      this component ever mounted — the acknowledgement modal, the login
//      screen, a tab click). Tracking only clicks inside the chyron would make
//      us STRICTER than the browser and suppress audio that already works.
//   2. Chrome hands back an AudioContext in the "suspended" state until that
//      gesture, and osc.start() on a suspended context is silent WITHOUT
//      throwing — so the failure is invisible. playChime resumes before it
//      schedules anything.
// Do not try to pre-warm past the policy itself: every workaround either
// violates it (and gets dropped anyway) or needs a gesture we cannot synthesise.

import { useState, useEffect, useRef, useCallback } from "react";
import { C, font, backendFetch } from "../core";

// ─── SETTINGS CONTRACT ────────────────────────────────────────────────────────
// ⚠⚠ DUPLICATED WITH src/SettingsPanel.jsx AND NOTHING ENFORCES PARITY. The keys,
// the defaults and the change-event name must move together. SettingsPanel is the
// writer for volume/keywords; this component writes the enabled flag too (its own
// toggle button), and both read. Same shape as the qs_position_sizes / usd()
// duplication already noted between WatchlistTab and ConvergenceBoard.
export const SQUAWK_ENABLED_KEY   = "qs_squawk_enabled";
export const SQUAWK_VOLUME_KEY    = "qs_squawk_volume";
export const SQUAWK_VOICE_KEY     = "qs_squawk_voice";
// ⚠⚠ A SEPARATE KEY, NOT SQUAWK_VOICE_KEY. That one holds a SYSTEM voice NAME
// resolved against speechSynthesis.getVoices(), and SettingsPanel writes it on
// every change. Sharing one key would mean a stored "af_heart" never matching a
// system voice (silently reverting Web Speech to the browser default) and the
// system picker overwriting the Kokoro choice. Two engines, two preferences.
export const SQUAWK_KOKORO_VOICE_KEY = "qs_squawk_kokoro_voice";
export const CHYRON_KEYWORDS_KEY  = "qs_chyron_keywords";

// localStorage is NOT reactive and the native `storage` event only fires in
// OTHER tabs — so a same-tab settings change would leave this component running
// on stale values until a remount. Both files dispatch this on every write and
// listen for it, which is what keeps the panel and the ticker in step live.
export const SETTINGS_EVENT = "qs-settings-change";

export function publishSettingsChange() {
  try { window.dispatchEvent(new CustomEvent(SETTINGS_EVENT)); } catch { /* ignore */ }
}

// ── Kokoro TTS ────────────────────────────────────────────────────────────────
// Neural TTS running locally in the browser. It is the PREFERRED engine but
// never a required one: Web Speech API remains the fallback and the squawk must
// stay fully functional if Kokoro never loads at all.
//
// ⚠⚠ WEBGPU ONLY, AND THAT IS A DELIBERATE NARROWING. kokoro-js can also run on
// WASM, but at 82M parameters that is slow enough to lag a live squawk on the
// hardware that lacks WebGPU. iOS Safari has no WebGPU and Android Chrome's is
// partial, so on most phones this check fails and the box speaks through Web
// Speech exactly as it does today — which is why every mobile defence in this
// file has to survive untouched.
// ⚠ navigator.gpu is presence-only. Actually requesting an adapter is async and
// would have to happen before we know whether to even try; from_pretrained
// failing is the real test, and it is caught.
const KOKORO_MODEL = "onnx-community/Kokoro-82M-v1.0-ONNX";

export const KOKORO_DEFAULT_VOICE = "af_heart";
export const KOKORO_VOICES = [
  { id: "af_heart",   label: "Professional F1" },
  { id: "af_bella",   label: "Professional F2" },
  { id: "am_adam",    label: "Professional M1" },
  { id: "am_michael", label: "Professional M2" },
];

// ⚠ Validated against the known list rather than trusted. A stale or
// hand-edited entry would otherwise be handed to generate() as a voice id and
// throw inside synthesis, where the failure is far less obvious.
export function loadKokoroVoice() {
  try {
    const raw = localStorage.getItem(SQUAWK_KOKORO_VOICE_KEY);
    return KOKORO_VOICES.some((v) => v.id === raw) ? raw : KOKORO_DEFAULT_VOICE;
  } catch { return KOKORO_DEFAULT_VOICE; }
}

export function kokoroSupported() {
  try { return typeof navigator !== "undefined" && !!navigator.gpu; } catch { return false; }
}

// Module-level singletons: the model is ~80MB and must be fetched and compiled
// ONCE per session, not once per component mount.
let _kokoro = null;          // the loaded instance, or null
let _kokoroPromise = null;   // in-flight load, so concurrent callers share one
let _kokoroFailed = false;   // sticky — never retry a hard failure in a loop

export function kokoroReady() { return _kokoro !== null; }
export function kokoroLoading() { return _kokoroPromise !== null && _kokoro === null && !_kokoroFailed; }

/** Begin (or join) the model load. Returns a promise resolving to the instance
 *  or null. NEVER rejects — a TTS engine failing is not a squawk failure.
 *
 *  ⚠⚠ FIRE-AND-FORGET BY DESIGN. The caller starts this and immediately speaks
 *  the current line through Web Speech. Awaiting an 80MB download before the
 *  first word would turn a market alert into a minute of silence, which is the
 *  one thing a squawk box must never do.
 */
export function initKokoro() {
  if (_kokoro || _kokoroFailed) return Promise.resolve(_kokoro);
  if (_kokoroPromise) return _kokoroPromise;
  if (!kokoroSupported()) {
    // ⚠ Not an error and nothing is surfaced to the user — Web Speech is a
    // perfectly good engine and this is the expected path on every iPhone.
    _kokoroFailed = true;
    return Promise.resolve(null);
  }
  // ⚠ Announced at the START as well as on completion — otherwise the
  // "initializing" indicator would only ever appear at the instant it is no
  // longer true.
  try { window.dispatchEvent(new CustomEvent(SETTINGS_EVENT)); } catch { /* ignore */ }
  _kokoroPromise = (async () => {
    // ⚠ DYNAMIC import, so Vite code-splits kokoro-js and onnxruntime into
    // their own chunk. A static import would put megabytes of inference runtime
    // into the main bundle for every user, including the ones whose browser
    // will never run it.
    const { KokoroTTS } = await import("kokoro-js");
    // fp32 is what the library recommends for the webgpu backend.
    const tts = await KokoroTTS.from_pretrained(KOKORO_MODEL, {
      dtype: "fp32",
      device: "webgpu",
    });
    _kokoro = tts;
    try { window.dispatchEvent(new CustomEvent(SETTINGS_EVENT)); } catch { /* ignore */ }
    return tts;
  })().catch((err) => {
    _kokoroFailed = true;
    _kokoro = null;
    // ⚠ Quiet. The user still has a working squawk box and there is nothing
    // for them to do about a WebGPU or model-fetch failure.
    try { console.warn("[squawk] Kokoro unavailable — using Web Speech API.", err); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(SETTINGS_EVENT)); } catch { /* ignore */ }
    return null;
  });
  return _kokoroPromise;
}

/** Synthesize `text` and return a playable HTMLAudioElement, or null.
 *  The caller owns playback so it can wire onended into the existing queue. */
export async function kokoroSynthesize(text, voiceId) {
  if (!_kokoro) return null;
  const raw = await _kokoro.generate(text, { voice: voiceId || KOKORO_DEFAULT_VOICE });
  // RawAudio → Blob → object URL. The caller revokes the URL on end/error.
  const url = URL.createObjectURL(raw.toBlob());
  const el = new Audio(url);
  el._objectUrl = url;
  return el;
}

// ⚠ Defaults apply only when the key is ABSENT. An explicitly stored "false" /
// "0" must survive — that is the whole point of persisting them.
export function loadSquawkEnabled() {
  try {
    const raw = localStorage.getItem(SQUAWK_ENABLED_KEY);
    return raw == null ? true : raw === "true";
  } catch { return true; }
}

// ⚠ Number(null) === 0, and so do "", false and []. A corrupt entry must not
// read as a legitimate "volume 0" (silently muting the box with no way to tell
// it apart from a deliberate mute), so the raw value is type-checked BEFORE it
// is coerced. A real stored 0 still round-trips.
export function loadSquawkVolume() {
  try {
    const raw = localStorage.getItem(SQUAWK_VOLUME_KEY);
    if (typeof raw !== "string" || raw.trim() === "") return 80;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 80;
    return Math.min(100, Math.max(0, Math.round(n)));
  } catch { return 80; }
}

// The chosen voice is stored by NAME, because SpeechSynthesisVoice objects are
// not serializable and their list differs by OS and browser. A name that does
// not exist on this device resolves to nothing and playback falls back to the
// browser default — see pumpVoice.
export function loadSquawkVoice() {
  try {
    const raw = localStorage.getItem(SQUAWK_VOICE_KEY);
    return typeof raw === "string" ? raw : "";
  } catch { return ""; }
}

export function loadKeywords() {
  try {
    const raw = localStorage.getItem(CHYRON_KEYWORDS_KEY);
    if (typeof raw !== "string") return [];
    return raw.split(",").map((k) => k.trim()).filter(Boolean);
  } catch { return []; }
}

// ─── TUNING ───────────────────────────────────────────────────────────────────
// One endpoint. The backend serves curated RSS + Finnhub's merger wire, already
// paywall-filtered, identity-checked, per-source floored, deduped and sorted
// newest-first — so this component does NO recency filtering and NO source
// floor of its own. The pool is served in backend order.
const NEWS_PATH             = "/api/news/chyron";
const REFRESH_MS            = 5 * 60 * 1000;   // pool refill; matches the 300s server cache
const POOL_MAX              = 50;              // matches the endpoint's own cap
const CAROUSEL_BATCH        = 15;              // headlines on screen per pass
const CAROUSEL_INTERVAL_MS  = 6 * 60 * 1000;   // 360s — one carousel advance
const SECONDS_PER_HEADLINE  = 8;     // scroll duration scales with the list
const MIN_DURATION_S        = 30;

// ── Platform ─────────────────────────────────────────────────────────────────
// ⚠⚠ MOBILE TTS ENGINES DIFFER FROM DESKTOP IN WAYS THAT ARE SILENT, NOT NOISY.
// Android Chrome truncates utterances beyond ~200 characters mid-word and then
// silently drops subsequent speak() calls; iOS Safari truncates at ~220 and
// suspends synthesis entirely when the tab is backgrounded. Neither throws.
// _chunkText keeps every utterance under 180 characters — safely below both
// limits — and the wider inter-utterance gap below stops either engine dropping
// rapid successive speak() calls.
const _UA = typeof navigator !== "undefined" ? (navigator.userAgent || "") : "";
const isAndroid = /android/i.test(_UA);
// ⚠⚠ THE maxTouchPoints CLAUSE IS NOT OPTIONAL. iPadOS 13+ reports a DESKTOP
// Macintosh user agent, so the bare /ipad|iphone|ipod/ test misses every modern
// iPad — which would then run the desktop 500ms gap on an engine that needs 900.
// A real Mac reports maxTouchPoints 0, so the pair is what separates them.
const isIOS =
  /ipad|iphone|ipod/i.test(_UA) ||
  (typeof navigator !== "undefined" &&
    navigator.maxTouchPoints > 1 &&
    /Macintosh/.test(_UA));
const isMobile = isAndroid || isIOS;

// ⚠ 900 on mobile, 500 on desktop. Both Android and iOS drop speak() calls that
// arrive too close behind the previous utterance's onend — the call returns
// normally and simply never produces audio.
const VOICE_GAP_MS          = isMobile ? 900 : 500;   // silence between utterances
// Max characters per utterance. See the platform note above: Android ~200,
// iOS ~220, so 180 clears both with margin for a multi-byte character.
const VOICE_CHUNK_MAX       = 180;
// iOS needs a moment after the tab returns to foreground before speak() takes.
const VISIBILITY_RESUME_MS  = 500;
const CHIME_HZ              = 440;
const CHIME_S               = 0.3;
const GESTURE_REQUEUE       = 3;     // headlines read back on the first gesture
const CONFIRM_TEXT          = "Squawk box active.";

// ── Voice ─────────────────────────────────────────────────────────────────────
// Exported so SettingsPanel's PREVIEW speaks with exactly the parameters the
// ticker will use — a preview that sounds different from the real thing is
// worse than no preview.
export const VOICE_RATE  = 0.95;
export const VOICE_PITCH = 1.0;

// ── Squawk watchdog tuning ────────────────────────────────────────────────────
// ⚠⚠ A watchdog that fires DURING normal speech is worse than no watchdog: it
// clears the latch mid-utterance, pushes the next headline into the engine's own
// queue, and then the real onend fires and advances a SECOND time — so the queue
// races, the 500ms gap disappears and playback garbles. This budget is an UPPER
// bound by design: it exists only to break a dead latch, never to pace playback.
//
// ⚠⚠ EXPRESSED PER WORD AT RATE 1.0 AND DIVIDED BY THE ACTUAL RATE, not a flat
// constant. Speech duration scales inversely with rate, so a fixed budget
// silently loses its margin the moment the rate drops — and it did: this was a
// flat 400 tuned for rate 1.1, and VOICE_RATE is now 0.95, ~14% slower. At 0.95
// the expression below yields 400 ms/word, identical to the old behaviour, and
// it stays correct if the rate is ever changed again. Typical TTS runs ~150-180
// wpm at rate 1.0 (330-400 ms/word), so 380 is a deliberate upper bound.
// ⚠ The user can now CHOOSE the voice, and system voices vary in pace — which
// is exactly why this can no longer be a number tuned against one of them.
const MS_PER_WORD_1X   = 380;    // ms per word at rate 1.0
const STALL_MS_PER_WORD = MS_PER_WORD_1X / VOICE_RATE;
// ⚠⚠ A WORD COUNT ALONE IS NOT SAFE FOR THE SQUAWK READOUT. A news headline runs
// 10-15 words; a squawk market-update utterance runs 40-60 — and long text is
// exactly where a word count is least representative, because the readout is
// number-dense and "0.4" is ONE word but THREE spoken ones ("zero point four").
// Words therefore UNDER-estimate precisely the utterances needing most headroom.
// A character estimate tracks that far better, so the budget is the GREATER of
// the two measures.
// ⚠⚠ TAKING THE MAX IS THE LOAD-BEARING PART, NOT AN OPTIMISATION. 60 ms/char is
// ~360 ms for a 6-character word, which is TIGHTER than the 400 ms/word budget
// shipping today — so adopting the character formula ALONE would have SHORTENED
// the watchdog for ordinary headlines and made it fire mid-utterance more often,
// the exact opposite of the intent. A max can only ever lengthen it.
// ⚠ Expressed at rate 1.0 and divided by VOICE_RATE — same convention, and the
// same reason, as MS_PER_WORD_1X: duration scales inversely with rate, so a
// constant tuned against one rate is a latent bug the moment the rate moves.
const MS_PER_CHAR_1X   = 60;     // ms per character at rate 1.0
const STALL_MS_PER_CHAR = MS_PER_CHAR_1X / VOICE_RATE;
const STALL_BUFFER_MS  = 3000;   // floor, added on top of whichever estimate wins
// ⚠ Kokoro's margin is much smaller than STALL_BUFFER_MS because it is added to
// a MEASURED duration rather than an estimate. The 3000ms above absorbs the
// error in a words-per-minute guess; this only has to absorb decode jitter and
// the gap between `play()` resolving and audio actually reaching the speaker.
const KOKORO_STALL_BUFFER_MS = 2000;
const KEEPALIVE_MS     = 10000;  // Chrome stalls the engine at ~15s of speech
// ⚠⚠ RAISED 15 → 50 FOR THE SQUAWK READOUT, WHICH ARRIVES AS ONE ATOMIC PUSH OF
// UP TO ~20 UTTERANCES. enqueueVoice DROPS THE WHOLE PUSH at cap, so the cap has
// to clear the largest single push or an entire readout vanishes with no tell.
// It must also stay above CAROUSEL_BATCH (15) for the original reason — a cap
// below the batch size silently discards part of every carousel advance. 50
// clears both, with room for a readout landing on a batch that has not drained.
const VOICE_QUEUE_CAP  = 50;

// Chyron quality filter — removes personal finance advice and low-signal retail
// content. News desk feed is unaffected.
// ⚠ `i['’]m` carries an explicit "m": the specced /i['']\s*\d+/ requires a digit
// straight after the apostrophe, so it matches "I' 62" and never the headline
// shape it was written for ("I'm 62 and have $1.4M...").
// ⚠⚠ EVERY PATTERN BELOW WAS DRY-RUN AGAINST 90 LIVE HEADLINES FROM ALL FIVE
// CONFIGURED SOURCES BEFORE IT SHIPPED. Exclusion patterns fail expensively and
// invisibly: a too-broad one deletes real market news and leaves no trace of
// what it removed. Re-run that dry run before adding to this list.
const CHYRON_EXCLUDE = [
  // ⚠ "401(k)" — the parens are why the original /401\s*k/ missed MarketWatch's
  // "a retired CPA with a $1.2 million 401(k)". Still matches bare "401k".
  /401\s*\(?\s*k\s*\)?/i,
  // ⚠ "Roth conversion" is as retail as "Roth IRA" and the original missed it.
  /roth\s*(ira|conversion|401)/i,
  /retirement\s*advice/i,
  /what\s*to\s*do\s*with/i,
  /i['’]m\s*\d+\s*(and|with)/i,
  /personal\s*finance/i,
  /dave\s*ramsey/i,
  /suze\s*orman/i,
  /times\s*of\s*india/i,
  /should\s*i\s*(invest|buy|sell)/i,

  // ── MarketWatch advice columns ────────────────────────────────────────────
  // That feed mixes genuine market coverage with a reader-advice column whose
  // headlines share one unmistakable shape: a first-person family money
  // question. Targeting the SHAPE rather than the subject is what keeps real
  // macro coverage of Social Security, Medicare and pensions on the ticker.
  //
  // ⚠⚠ THE FAMILY LIST IS THE WHOLE POINT — a bare /\bmy\b/ would be a disaster.
  // Wolf Street publishes "My Thoughts about those July Retail Sales", which is
  // exactly the kind of headline this ticker exists to carry. Verified kept.
  /\bmy\s+(husband|wife|spouse|partner|son|daughter|mother|father|mom|dad|parents|brother|sister|in-laws|boyfriend|girlfriend|friend)\b/i,
  // "I'm 63, a retired CPA…" — the age disclosure that opens these columns.
  // Two digits, so a percentage or a year cannot trip it.
  /\bi['’]m\s+\d{2}\b/i,
  // First-person money questions. The verb list is deliberate: bare "can I" /
  // "should I" appears in ordinary market commentary, "can I claim" does not.
  /\b(can|should|do)\s+i\s+(claim|retire|take|keep|leave|pay|withdraw|afford|stop|split|cash|give|roll)\b/i,
  // Unambiguously retail instruments. Social Security / Medicare / Medicaid are
  // deliberately ABSENT — they are legitimate macro subjects, and the columns
  // that use them are already caught by the family and question patterns above.
  /\b(annuity|annuities|alimony|prenup|inheritance|inherited\s+ira|estate\s+plan\w*)\b/i,

  // ── Lifestyle / consumer wellness ─────────────────────────────────────────
  // MarketWatch and CNBC both run diet-and-wellness features between market
  // pieces ("The Mediterranean diet is the best out there…").
  //
  // ⚠⚠ FOOD AND HEALTH WORDS ARE MARKET WORDS. This is a far more dangerous
  // category to pattern than personal finance, because the same vocabulary
  // carries real sector news: weight-loss drugs are the biggest pharma story
  // running, "recipe for disaster" is stock financial commentary, the
  // Mediterranean is a shipping lane, plant-based is a food-sector thesis and
  // Lululemon sells yoga pants. Every pattern here is therefore anchored to a
  // phrase that only occurs in a lifestyle context — a NAMED diet, not the word
  // "diet"; "recipes" plural, never the singular; "weight-loss tips", never
  // "weight-loss drug". Verified against 14 adversarial market headlines.
  /\b(mediterranean|keto|ketogenic|paleo|vegan|vegetarian|plant[-\s]based|low[-\s]carb|dash)\s+diet\b/i,
  /\bintermittent\s+fasting\b/i,
  /\b(healthy\s+eating|nutrition\s+tips|superfoods?|meal\s+prep(?:ping)?|home\s+cooking|cooking\s+at\s+home)\b/i,
  /\brecipes\b/i,
  /\b(weight[-\s]loss\s+tips|diet\s+plan|eating\s+habits)\b/i,
];

function passesChyronFilter(source, headline, summary) {
  const hay = `${source || ""} ${headline || ""} ${summary || ""}`.toLowerCase();
  return !CHYRON_EXCLUDE.some((re) => re.test(hay));
}

// Take `n` items starting at `start`, wrapping past the end of the pool. Deduped
// because a pool shorter than the batch would otherwise repeat items within one
// screen — and duplicate ids mean duplicate React keys and a headline spoken
// twice.
function sliceWrap(arr, start, n) {
  if (!arr.length) return [];
  const s = ((start % arr.length) + arr.length) % arr.length;
  const out = [...arr.slice(s, s + n)];
  if (out.length < n) out.push(...arr.slice(0, Math.min(n - out.length, s)));
  const seen = new Set();
  return out.filter((h) => (seen.has(h.id) ? false : (seen.add(h.id), true)));
}

// ⚠ A LITERAL AMBER, DELIBERATELY. C.amber in this palette is #00FF87 (Quantum
// Emerald) — using the token would render keyword hits the same green as every
// other accent on the board and lose the distinction entirely.
const KEYWORD_COLOR = "#FFB300";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function matchesKeywords(headline, summary, keywords) {
  if (!keywords.length) return false;
  const hay = `${headline || ""} ${summary || ""}`.toLowerCase();
  return keywords.some((k) => {
    const needle = k.toLowerCase().trim();
    return needle ? hay.includes(needle) : false;
  });
}

// Split `text` into utterances of at most `maxChars` characters, preferring the
// least audible seam available: sentence, then clause, then word, then — only
// when a single token is longer than the limit — a hard cut.
//
// ⚠⚠ THIS EXISTS BECAUSE THE FAILURE IT PREVENTS IS INVISIBLE. An over-long
// utterance on Android Chrome is truncated mid-word and the engine then stops
// honouring speak() for the rest of the session; nothing throws, onend still
// fires, and the queue drains normally into silence. There is no error to catch
// and no state to inspect — the only defence is not exceeding the limit.
// ⚠ Boundaries are searched in a window of maxChars + 1 so a seam sitting
// exactly ON the limit is still usable; every returned chunk is <= maxChars.
// ⚠ The sentence and clause cuts keep their punctuation with the chunk that
// precedes them ("...cools." not "...cools" + ". ..."), because a chunk ending
// on a bare word reads as an interruption while one ending on a full stop reads
// as a pause — which is exactly what the gap between utterances sounds like.
export function _chunkText(text, maxChars = VOICE_CHUNK_MAX) {
  const src = String(text == null ? "" : text).trim();
  if (!src) return [];
  if (src.length <= maxChars) return [src];

  const out = [];
  let rest = src;
  while (rest.length > maxChars) {
    const window_ = rest.slice(0, maxChars + 1);
    let cut = -1;
    // 1. sentence boundary — keep the period with this chunk
    const sentence = window_.lastIndexOf(". ");
    if (sentence > 0) cut = sentence + 1;
    // 2. clause boundary — keep the comma with this chunk
    if (cut <= 0) {
      const clause = window_.lastIndexOf(", ");
      if (clause > 0) cut = clause + 1;
    }
    // 3. word boundary
    if (cut <= 0) {
      const word = window_.lastIndexOf(" ");
      if (word > 0) cut = word;
    }
    // 4. hard split — a single token longer than the limit. Last resort: it
    //    breaks a word, but a broken word is audible while a dropped engine is
    //    not, and this is the only branch that can still make progress.
    if (cut <= 0) cut = maxChars;
    out.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) out.push(rest);
  // ⚠ `cut` is always >= 1, so `rest` shrinks by at least one character every
  // iteration and the loop cannot spin. The filter is for a seam that trimmed
  // to nothing (e.g. runs of whitespace), not for termination.
  return out.filter(Boolean);
}

// ⚠ Fires on keyword hits only. A fresh AudioContext per tone (closed on the
// oscillator's onended) rather than one long-lived context: chimes are rare and
// a suspended context left open across a session is the thing that goes stale.
function playChime(volume01) {
  if (!(volume01 > 0)) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const close = () => { try { ctx.close(); } catch { /* already closed */ } };

    // ⚠⚠ EVERYTHING IS SCHEDULED AFTER resume() RESOLVES, NOT BEFORE. A
    // suspended context's currentTime does not advance, so nodes scheduled
    // against it before the resume land in the past and the tone is dropped —
    // silently, with nothing thrown. This is the actual mechanism behind a
    // chime that "does nothing" even once audio is unlocked.
    const fire = () => {
      try {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = CHIME_HZ;
        // ⚠ exponentialRampToValueAtTime cannot ramp from or to exactly 0 — it
        // throws. Floor both ends.
        gain.gain.setValueAtTime(Math.max(0.0001, volume01), ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + CHIME_S);
        osc.onended = close;
        osc.start();
        osc.stop(ctx.currentTime + CHIME_S);
      } catch { close(); }
    };

    if (ctx.state === "suspended" && typeof ctx.resume === "function") {
      ctx.resume().then(fire).catch(close);
    } else {
      fire();
    }
  } catch { /* unsupported — silent by design */ }
}

// ─── SQUAWK READOUT ───────────────────────────────────────────────────────────
// A periodic spoken market readout, assembled from live platform data and read
// through the SAME voice queue as the headlines, so pumpVoice paces both alike.
// Six sections, each fetched independently and each SKIPPED ENTIRELY when its
// source is empty — a readout that says "no data" six times is worse than a
// short one, and an empty section is the normal state outside market hours.
//
// ⚠⚠ EVERY FETCH BELOW IS INDEPENDENT OF THE CHYRON HEADLINE FETCH. Nothing here
// touches NEWS_PATH, the pool, the carousel, poolIndexRef or seenIdsRef. Section
// 6 READS the batch already on screen (passed in) rather than re-fetching it.
const SQUAWK_SNAPSHOT_PATH    = "/api/snapshot?symbols=SPY,QQQ,DIA";
const SQUAWK_QSFG_PATH        = "/api/sentiment/qsfg";
const SQUAWK_BULLBEAR_PATH    = "/api/sentiment/bullbear";
const SQUAWK_STRESS_PATH      = "/api/sentiment/stress";
const SQUAWK_CRACK_PATH       = "/api/altdata/crack-spreads";
// ⚠⚠ ENERGY FUTURES CLOSE AND THE SQUAWK DOES NOT. The endpoint caches 900s
// and serves its last good copy on failure, so over a weekend it will happily
// return Friday's settle with a Friday timestamp. Reading that aloud on Monday
// morning as "3-2-1 crack spread at 58 dollars" presents a two-day-old number
// as live. Beyond this age the section is silent — a missing line is honest,
// a stale one spoken with confidence is not.
const SQUAWK_CRACK_MAX_AGE_MS = 2 * 60 * 60 * 1000;   // 2 hours
const SQUAWK_DARKPOOL_PATH    = "/api/darkpool/scan";
// ⚠ /api/stream/uo/market DOES NOT EXIST. The market-wide UO key is served by
// /api/stream/unusual, which returns SIX COUNTERS AND NO PRINTS — it cannot rank
// anything. /api/stream/unusual/recent is the row source: every stream:uo:{sym}
// list aggregated and premium-ranked, which is what "top 5 by premium" needs.
const SQUAWK_UO_PATH          = "/api/stream/unusual/recent";
// ⚠ /api/altdata/convergence does not exist either; convergence is a stream key.
const SQUAWK_CONVERGENCE_PATH = "/api/stream/convergence";
const SQUAWK_AUTOCALL_PATH    = "/api/altdata/autocallable/alerts";
// ⚠ SIX HOURS, NOT THE ENDPOINT'S TWENTY-FOUR. The tape keeps a day so the
// board and the badge can show what happened this session; the squawk is a LIVE
// readout and a barrier that broke before lunch is no longer news by the close.
const SQUAWK_AUTOCALL_HOURS   = 6;
// ⚠ Three per cycle. The readout is spoken end to end, so an unbounded section
// on a volatile day would push news and flights past the point anyone listens.
const SQUAWK_AUTOCALL_N       = 3;
const SQUAWK_TOP_N            = 5;
const SQUAWK_NEWS_N           = 5;
// ⚠ SEPARATE FROM SQUAWK_TOP_N ON PURPOSE. Widening SQUAWK_TOP_N to reach the
// flights cap would also lengthen the dark-pool and unusual-options sections,
// which share it. Flight events are the least time-critical thing in the readout
// and the queue is already 80-120s; three is the whole budget they get.
const SQUAWK_FLIGHTS_N        = 3;
const SQUAWK_FLIGHTS_PATH     = "/api/altdata/aircraft/events";
// ⚠ SECONDS, matching the event timestamp's own unit — see squawkFlightEvents.
const SQUAWK_FLIGHT_MAX_AGE_S = 86400;

// ⚠⚠ VIX IS DELIBERATELY ABSENT FROM THE SNAPSHOT SYMBOL LIST. It is an INDEX,
// not an Alpaca-tradable equity, so /v2/stocks/snapshots returns nothing for it
// and it would read as a permanent data outage. The level comes from
// /api/sentiment/qsfg's `vix` component instead, which carries the raw index
// value alongside its 0-100 score.
const SQUAWK_INDEX_NAMES = [
  ["SPY", "S&P 500"],
  ["QQQ", "Nasdaq"],
  ["DIA", "Dow Jones"],
];

// ⚠⚠ KEYED ON THE VALUES THE BACKEND ACTUALLY EMITS. _classify_tier — duplicated
// in routers/market_data.py and workers/darkpool.py and asserted identical —
// emits exactly INSTITUTIONAL_ACCUMULATION / SIGNIFICANT_FLOW /
// ELEVATED_BLOCK_ACTIVITY. SIGNIFICANT_BUYING and ELEVATED_ACTIVITY are not
// values this platform produces; keying on them would leave two of the three
// tiers unlabelled.
// ⚠ "significant flow", not "significant buying": the tier requires DIRECTIONAL
// flow but not a buy side — net_bias is independently BUYER or SELLER — so
// calling it buying would assert a direction the tier does not carry.
// ⚠ An unrecognised tier degrades to its own name with the underscores spoken as
// spaces, so a tier added later is read out rather than silently dropped.
const SQUAWK_TIER_LABELS = {
  INSTITUTIONAL_ACCUMULATION: "institutional accumulation",
  SIGNIFICANT_FLOW: "significant flow",
  ELEVATED_BLOCK_ACTIVITY: "elevated block activity",
};

// ── Cadence ───────────────────────────────────────────────────────────────────
const SQUAWK_MARKET_MS    = 3  * 60 * 1000;   // 09:30-16:00 ET
const SQUAWK_EXTENDED_MS  = 10 * 60 * 1000;   // pre/post, and the closed re-check
// ⚠ Not 0. Toggling the box on also queues a chime and a spoken confirmation
// (see toggleSquawk), and the confirmation is pushed ~360 ms after the click.
// Firing the readout at 0 ms would put ~20 utterances into the queue AHEAD of it,
// so the box would read the entire market update before saying it was switched
// on. This clears that window.
const SQUAWK_IMMEDIATE_MS = 1500;

function squawkEtParts(date = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = {};
  for (const p of fmt.formatToParts(date)) parts[p.type] = p.value;
  // ⚠ Midnight can still come back as "24" in some engines even under h23.
  return {
    weekday: parts.weekday,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    // ⚠ The ET CALENDAR DAY, not the UTC one. The midday stress read fires once
    // per session and is deduped on this key; UTC would roll it over at 20:00 ET
    // and hand the same session a second midday read.
    day: `${parts.year}-${parts.month}-${parts.day}`,
  };
}

// "market" | "extended" | "closed".
// ⚠⚠ ET, NOT UTC, and resolved through Intl rather than a fixed offset so it
// follows DST across both transitions without a table — the same rule, for the
// same reason, as _is_peak_window in the backend's altdata.py.
// ⚠ Extended bounds are the standard US extended session, 04:00-20:00 ET; 04:00
// is also the pre-market open the dark-pool archive beat is built around.
// ⚠⚠ US MARKET HOLIDAYS ARE NOT HANDLED — a holiday reads as an ordinary weekday
// and squawks on the market cadence. /api/markets/calendar exists and could gate
// it; this is a deliberate omission to keep the readout free of a sixth fetch,
// not an oversight.
export function _isMarketHours() {
  let p;
  // ⚠ A broken Intl falls back to EXTENDED, deliberately between the two errors:
  // "closed" would kill the feature silently with nothing on screen to explain
  // it, and "market" would run the aggressive 3-minute cadence around the clock.
  try { p = squawkEtParts(); } catch { return "extended"; }
  if (!Number.isFinite(p.hour) || !Number.isFinite(p.minute)) return "extended";
  if (p.weekday === "Sat" || p.weekday === "Sun") return "closed";
  const mins = p.hour * 60 + p.minute;
  if (mins >= 9 * 60 + 30 && mins < 16 * 60) return "market";
  if (mins >= 4 * 60 && mins < 20 * 60) return "extended";
  return "closed";
}

// ⚠ "closed" keeps the EXTENDED interval rather than stopping: the timer is the
// only thing that can notice the session opening, so a stopped clock would leave
// the box silent until the next remount.
export function squawkIntervalMs(phase) {
  return phase === "market" ? SQUAWK_MARKET_MS : SQUAWK_EXTENDED_MS;
}

// ── Fetch + speech formatting ─────────────────────────────────────────────────
async function squawkGet(path) {
  try {
    const res = await backendFetch(path, path);
    if (!res?.ok) return null;
    return await res.json();
  } catch { return null; }
}

// ⚠ "up"/"down" spoken as words — a sign character is not pronounced at all.
// ⚠ A move that rounds to 0.0 at one decimal is named "unchanged": "up 0.0
// percent" reads as a broken feed rather than as a flat tape.
function spokenPct(n) {
  if (!Number.isFinite(n)) return "";
  const v = Math.abs(n);
  if (v < 0.05) return "unchanged";
  return `${n > 0 ? "up" : "down"} ${v.toFixed(1)} percent`;
}

// $3.7B → "3.7 billion dollars" · $406M → "406 million dollars".
// ⚠ Billions keep one decimal — the gap between $3.1B and $3.7B is meaningful —
// while millions round whole, because "406 point 3 million" is noise at that
// scale. Thousands exist only as a floor; the lowest dark-pool tier is $1M.
function spokenMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "";
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)} billion dollars`;
  if (v >= 1e6) return `${Math.round(v / 1e6)} million dollars`;
  if (v >= 1e3) return `${Math.round(v / 1e3)} thousand dollars`;
  return `${Math.round(v)} dollars`;
}

// ⚠ The stream writes option_type as literal "call"/"put" (uo_classifier parses
// the OCC symbol), but the batch scanner can carry "C"/"P" — and the tier string
// (`EXTREME_CALL`) always names the side, so it is the fallback rather than
// dropping a row whose type field is in the other shape.
function optionSide(row) {
  const t = String(row?.option_type || "").trim().toLowerCase();
  if (t.startsWith("c")) return "call";
  if (t.startsWith("p")) return "put";
  const sig = String(row?.signal || "").toUpperCase();
  if (sig.includes("CALL")) return "call";
  if (sig.includes("PUT")) return "put";
  return "";
}

// ── SECTION 1 — market overview ───────────────────────────────────────────────
async function squawkMarketSection() {
  const [snapRes, qsfgRes, bbRes, crackRes] = await Promise.allSettled([
    squawkGet(SQUAWK_SNAPSHOT_PATH),
    squawkGet(SQUAWK_QSFG_PATH),
    squawkGet(SQUAWK_BULLBEAR_PATH),
    squawkGet(SQUAWK_CRACK_PATH),
  ]);
  const snap = snapRes.status === "fulfilled" ? snapRes.value : null;
  const qsfg = qsfgRes.status === "fulfilled" ? qsfgRes.value : null;
  const bb   = bbRes.status   === "fulfilled" ? bbRes.value   : null;
  const crack = crackRes.status === "fulfilled" ? crackRes.value : null;

  // ⚠ Alpaca has served this payload both flat and wrapped in `snapshots`.
  // Accept either rather than betting the whole section on one shape.
  const bySym = (snap && (snap.snapshots || snap)) || {};

  const parts = [];
  for (const [sym, name] of SQUAWK_INDEX_NAMES) {
    const row = bySym[sym];
    // ⚠⚠ THERE IS NO changePct FIELD ON THIS ENDPOINT — it proxies Alpaca's raw
    // snapshot. The move is derived here exactly as WatchlistTab derives it:
    // last trade against the PREVIOUS session's close. dailyBar.c is the
    // fallback for a symbol with no print yet (pre-market on a thin name).
    const last = row?.latestTrade?.p ?? row?.dailyBar?.c;
    const prev = row?.prevDailyBar?.c;
    if (!(Number.isFinite(last) && Number.isFinite(prev) && prev > 0)) continue;
    const said = spokenPct(((last - prev) / prev) * 100);
    if (said) parts.push(`${name} ${said}.`);
  }

  const vix = Number(qsfg?.components?.vix?.value);
  // ⚠ Omitted rather than spoken as "null" when the component did not resolve —
  // _comp_vix returns None whenever fred:VIXCLS:* is cold, which this log records
  // happening for whole sessions.
  if (Number.isFinite(vix)) parts.push(`VIX at ${vix.toFixed(1)}.`);

  // ⚠⚠ A SEPARATE UTTERANCE, NOT APPENDED TO `parts`. The market line is
  // already three index moves plus VIX; folding Bull/Bear in would push it past
  // the length where Android's synth reliably completes a single utterance, and
  // this log records exactly that failure mode. It also lets the whole reading
  // survive if the Bull/Bear endpoint is the one that is down.
  const bbLines = [];
  // ⚠⚠ typeof === "number", NOT Number(). Number(null) is 0 and 0 is finite, so
  // a `Number(bb?.score)` guard let the UNAVAILABLE payload — which carries
  // score: null — speak "Q S Bull Bear at 0.0 out of 10", i.e. an outage read
  // aloud as CAPITULATION, the exact fabricated contrarian BUY the backend
  // renormalization exists to prevent. A string "7.1" also coerced through.
  const bbScore = typeof bb?.score === "number" ? bb.score : NaN;
  if (Number.isFinite(bbScore)) {
    // ⚠ "Q S" with the space is deliberate — written solid, every synth tested
    // attempts to pronounce "QS" as a syllable.
    const bbLabel = String(bb?.label || "").trim().toLowerCase();
    // ⚠ The contrarian read is spoken because the NUMBER ALONE IS AMBIGUOUS TO A
    // LISTENER: on this scale a high reading is a warning, the opposite of what
    // "bullish" sounds like, and a listener has no colour scale to correct them.
    const bbSignal =
      bb?.signal === "CONTRARIAN_BEARISH" ? " Contrarian sell signal." :
      bb?.signal === "CONTRARIAN_BULLISH" ? " Contrarian buy signal." : "";
    bbLines.push({
      text: `Q S Bull Bear at ${bbScore.toFixed(1)} out of 10` +
            `${bbLabel ? `, ${bbLabel}` : ""}.${bbSignal}`,
      section: "market",
    });
  }

  // ⚠⚠ A THIRD SEPARATE UTTERANCE, for the same reason bbLines is separate (see
  // the note above it): the market line already carries three index moves plus
  // VIX, and appending more pushes it past the length where Android's synth
  // reliably completes a single utterance. It also means a crack-spread outage
  // costs only its own line.
  const crackLines = [];
  // ⚠ typeof === "number", NOT Number(). Number(null) is 0 and 0 is finite, so a
  // coercion guard would let a null spread speak "at 0 dollars per barrel" — a
  // reading that on this scale means refiners are running at a catastrophic loss.
  // The same trap the Bull/Bear guard above exists to prevent.
  const crackVal = typeof crack?.crack_321 === "number" ? crack.crack_321 : NaN;
  const crackAt = crack?.timestamp ? Date.parse(crack.timestamp) : NaN;
  // ⚠ Both guards required. A finite number with no usable timestamp cannot be
  // shown to be current, and an unparseable stamp yields NaN — for which every
  // comparison is false, so the age test must be written to REJECT that case
  // rather than fall through it.
  const crackFresh = Number.isFinite(crackAt)
    && (Date.now() - crackAt) <= SQUAWK_CRACK_MAX_AGE_MS;
  if (Number.isFinite(crackVal) && crackFresh) {
    const regime = String(crack?.regime || "").trim();
    // ⚠ Only the elevated case is spoken. "Normal range" adds nothing a listener
    // can act on, and "Historically stressed" is carried by the STRESS regime
    // label itself — repeating it would be two claims about one reading.
    const elevated = crack?.percentile_context === "Historically elevated"
      ? " Historically elevated relative to the two-year median." : "";
    crackLines.push({
      text: `Energy markets. 3 2 1 crack spread at ${crackVal.toFixed(1)} dollars per barrel.`
            + `${regime ? ` ${regime}.` : ""}${elevated}`,
      section: "market",
    });
  }

  if (!parts.length) return [...bbLines, ...crackLines];
  return [{ text: `Market update. ${parts.join(" ")}`, section: "market" },
          ...bbLines, ...crackLines];
}

// ── SECTION 1b — market stress monitor ────────────────────────────────
// ⚠⚠ THIS SECTION IS SILENT MOST OF THE TIME AND THAT IS THE DESIGN. The readout
// re-runs every three minutes; a stress level that simply persists is not news,
// and announcing "level one divergence" twenty times a session would train the
// listener to tune out the one alert that matters. It speaks on a CHANGE, on
// FIRST detection, and once at midday as a deliberate re-anchor.
const SQUAWK_STRESS_TEXT = {
  1: "Stress monitor alert. Level one divergence. Credit stress forming while equity remains calm.",
  2: "Stress monitor alert. Level two. Institutional distribution confirmed.",
  3: "Stress monitor alert. Level three. Market capitulation conditions detected.",
};
const SQUAWK_STRESS_MIDDAY_HOUR = 12;

export async function squawkStressSection(state) {
  const st = state && typeof state === "object"
    ? state
    // ⚠ A throwaway on the default path announces every cycle. The component
    // always passes its ref; this exists so the function is callable in isolation
    // without reaching for module-level mutable state.
    : { lastLevel: null, lastMidday: null };

  const data = await squawkGet(SQUAWK_STRESS_PATH);
  // ⚠ typeof === "number", not Number(): Number(null) is 0, and 0 is a REAL
  // level here — an outage would silently record itself as "normal" and then
  // suppress the next genuine level-1 alert as a non-change.
  const level = typeof data?.level === "number" ? data.level : null;
  if (level == null) return [];

  // Calibrating is a live, correct reading with nothing to say. It must not
  // touch lastLevel either, or the first real detection after calibration ends
  // would compare against a fabricated 0.
  if (data?.calibrating === true) return [];

  let now = null;
  try { now = squawkEtParts(); } catch { now = null; }
  const isMidday = !!now && now.hour === SQUAWK_STRESS_MIDDAY_HOUR
    && st.lastMidday !== now.day;

  const changed = st.lastLevel !== level;

  // ⚠⚠ RECORD THE LEVEL BEFORE THE LEVEL-0 BAIL, NOT AFTER. Level 0 is skipped
  // in the squawk, but it still has to be remembered: without this line a
  // 1 → 0 → 1 round trip leaves lastLevel at 1 and the re-escalation reads as
  // "no change", swallowing the alert exactly when it has become interesting
  // again.
  st.lastLevel = level;
  if (isMidday) st.lastMidday = now.day;

  const text = SQUAWK_STRESS_TEXT[level];
  if (!text) return [];                 // level 0 — skipped entirely, as specced
  if (!changed && !isMidday) return [];
  return [{ text, section: "stress" }];
}

// ── SECTION 2 — dark pool ─────────────────────────────────────────────────────
async function squawkDarkpoolSection() {
  // ⚠⚠ NO `symbols=` PARAMETER, AND THAT IS DELIBERATE. That branch runs a live
  // per-symbol REST fan-out on demand; omitting it serves the */30 platform beat
  // cache, which is both free and far wider (218+ symbols vs a handful).
  const data = await squawkGet(SQUAWK_DARKPOOL_PATH);
  const rows = Array.isArray(data?.results) ? data.results : [];
  const top = [...rows]
    .sort((a, b) => (Number(b?.tier_score) || 0) - (Number(a?.tier_score) || 0))
    .slice(0, SQUAWK_TOP_N);

  const out = [];
  for (const r of top) {
    const sym = String(r?.symbol || "").trim();
    if (!sym) continue;
    const tier = String(r?.tier || "").toUpperCase();
    const label = SQUAWK_TIER_LABELS[tier] || tier.toLowerCase().replace(/_/g, " ");
    const money = spokenMoney(r?.total_notional);
    const bits = [sym, label, money].filter(Boolean);
    if (bits.length < 2) continue;   // a bare ticker says nothing worth hearing
    out.push({ text: `${bits.join(", ")}.`, section: "darkpool" });
  }
  if (!out.length) return [];
  return [{ text: "Dark pool activity.", section: "darkpool" }, ...out];
}

// ── SECTION 3 — unusual options ───────────────────────────────────────────────
async function squawkOptionsSection() {
  const data = await squawkGet(SQUAWK_UO_PATH);
  const rows = Array.isArray(data?.results) ? data.results : [];
  const top = [...rows]
    .sort((a, b) => (Number(b?.total_premium) || 0) - (Number(a?.total_premium) || 0))
    .slice(0, SQUAWK_TOP_N);

  const out = [];
  for (const r of top) {
    const text = squawkOptionLine(r);
    if (text) out.push({ text, section: "options" });
  }
  if (!out.length) return [];
  return [{ text: "Unusual options activity.", section: "options" }, ...out];
}

// One spoken options row, or null when it would say nothing (the skip rule).
// ⚠ "sweep" ONLY when the stream flagged the print as one (is_sweep === true:
// it carried an ISO condition code). Every row used to be called a sweep. False,
// missing or anything else gets the neutral wording — a missing flag is unknown.
function squawkOptionLine(r) {
  // ⚠ `ticker` — the endpoint's normalizer renames the stream's `underlying`.
  const sym = String(r?.ticker || "").trim();
  const side = optionSide(r);
  const money = spokenMoney(r?.total_premium);
  if (!sym || !side || !money) return null;
  const what = r?.is_sweep === true ? `${side} sweep` : `unusual ${side} activity`;
  return `${sym}, ${what}, ${money} premium.`;
}

// ── Convergence trigger labels ────────────────────────────────────────────────
// ⚠⚠ KEYED ON WHAT THE DETECTOR ACTUALLY EMITS, measured across all 18,210 rows
// of convergence_history — NOT on the domain names the endpoint's other fields
// use. `signals_fired` does not contain "dark_pool" or "unusual_options"; it
// contains directional descriptors, and some of them are COMPOUND:
//
//   dark_pool_neutral 5643   unusual_puts 4169   dark_pool_seller 4054
//   congressional 3931   dark_pool_buyer+unusual_calls 3775   midpoint_cluster 2494
//   dark_pool_seller+unusual_puts 2172   dark_pool_buyer 2095   legislation 1973
//   earnings_0d…earnings_7d ~75   autocall_at_barrier 13
//
// ⚠ Direction is already IN the member name, so nothing has to be inferred from
// a separate field: dark_pool_buyer is accumulation, _seller is distribution,
// _neutral is neither and must not claim either.
// ⚠ fda_catalyst has fired 0 times in 18,210 events. No label is invented for
// it — if it ever fires it falls through to the underscore-stripping default,
// which reads acceptably, rather than shipping a guess about its shape.
const CONVERGENCE_LABELS = {
  dark_pool_buyer: "dark pool accumulation",
  dark_pool_seller: "dark pool distribution",
  dark_pool_neutral: "dark pool activity",
  unusual_calls: "unusual call sweep",
  unusual_puts: "unusual put sweep",
  midpoint_cluster: "midpoint cluster",
  congressional: "congressional activity",
  legislation: "legislative activity",
  autocall_at_barrier: "barrier proximity",
};

// ⚠ Bands calibrated to the OBSERVED range, not to 0-100. The detector's scores
// run 40-75 (mean 45.2): across 18,210 events only 36 ever reached 70, so a
// ">= 70 = high conviction" rule would have been dead text spoken roughly twice
// a year. At 65 the qualifier stays selective and can actually occur.
const CONVERGENCE_HIGH = 65;
const CONVERGENCE_MODERATE = 50;
const CONVERGENCE_READ_MAX = 5;      // read the top 5; COUNT reports all of them
const CONVERGENCE_CLOSER_MIN = 3;    // closing pointer only once there is depth

/** One signals_fired member → spoken label. Never returns underscores. */
function convergenceLabel(member) {
  const m = String(member || "").trim().toLowerCase();
  if (!m) return "";
  if (CONVERGENCE_LABELS[m]) return CONVERGENCE_LABELS[m];
  // earnings_0d … earnings_7d — the number is the days out, and "in 0 days" is
  // not something anyone says.
  const earn = m.match(/^earnings_(\d+)d$/);
  if (earn) {
    const d = Number(earn[1]);
    if (d === 0) return "earnings today";
    return d === 1 ? "earnings in one day" : `earnings in ${d} days`;
  }
  // ⚠ Unknown members still have to be SPEAKABLE. An underscore reads as a
  // stumble, so the raw value is only ever spoken with them stripped.
  return m.replace(/_/g, " ").trim();
}

/** signals_fired → ordered, de-duplicated spoken labels.
 *
 *  ⚠⚠ COMPOUND MEMBERS ARE SPLIT ON "+". "dark_pool_buyer+unusual_calls" is two
 *  independent findings the detector happened to record in one string; read
 *  whole it becomes "dark pool buyer plus unusual calls", which is neither
 *  English nor accurate about how many things converged.
 *  ⚠ De-duplicated because a compound and its own parts can both appear in one
 *  array, and "converging on dark pool accumulation and dark pool accumulation"
 *  is worse than saying it once.
 */
function convergenceLabels(signalsFired) {
  const out = [];
  const list = Array.isArray(signalsFired) ? signalsFired : [];
  for (const raw of list) {
    for (const part of String(raw || "").split("+")) {
      const label = convergenceLabel(part);
      if (label && !out.includes(label)) out.push(label);
    }
  }
  return out;
}

/** ["a"] → "a";  ["a","b"] → "a and b";  ["a","b","c"] → "a, b, and c" */
function spokenList(items) {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

// ── SECTION 4 — convergence ───────────────────────────────────────────────────
async function squawkConvergenceSection() {
  // ⚠ This endpoint returns a BARE ARRAY, not an object with a results key —
  // unlike every other endpoint read in this file.
  const data = await squawkGet(SQUAWK_CONVERGENCE_PATH);
  const rows = Array.isArray(data) ? data.filter(Boolean) : [];
  if (!rows.length) return [];   // silence, never "zero active"

  // ⚠ The COUNT is of every active signal, not of the five that get read. The
  // readout is a summary of the strongest; the number is what the platform is
  // actually watching, and quoting five when twenty are live would understate
  // the board the listener is being pointed at.
  const n = rows.length;
  const out = [{
    text: `${n} active convergence ${n === 1 ? "signal" : "signals"}. Top movers this session:`,
    section: "convergence",
  }];

  // ⚠⚠ TOP BY SCORE, NOT BY TIME. The endpoint sorts by triggered_at desc, so
  // rows[0] is the most RECENT event and not the strongest — a two-hour-old
  // seventy beats a five-minute-old forty, because score is the conviction
  // measure and recency is not.
  // ⚠⚠ DE-DUPED BY SYMBOL, defence in depth. The endpoint already keeps one
  // event per ticker (a `seen` set in stream_convergence), so today this can
  // never drop anything — but the reader was relying on that silently, and a
  // reader that depends on a writer's behaviour without asserting it is exactly
  // how this codebase has broken before. "Top 5" should mean five NAMES; the
  // failure mode without it is the box saying "NVDA converging on…" three times
  // in a row, which sounds broken whatever the data says.
  // ⚠ Sort BEFORE de-duping so the survivor is the highest-scoring row for that
  // symbol, not whichever happened to arrive first.
  const seenSym = new Set();
  const top = [];
  for (const r of [...rows].sort(
    (a, b) => (Number(b?.score) || 0) - (Number(a?.score) || 0)
  )) {
    const s = String(r?.symbol || "").trim().toUpperCase();
    if (!s || seenSym.has(s)) continue;
    seenSym.add(s);
    top.push(r);
    if (top.length >= CONVERGENCE_READ_MAX) break;
  }

  let spoken = 0;
  for (const r of top) {
    const sym = String(r?.symbol || "").trim().toUpperCase();
    const score = Number(r?.score);
    // ⚠ A row with no symbol or an unusable score is SKIPPED, not spoken with a
    // gap in it. Number(null) is 0 and finite, so the score is type-checked
    // before it is trusted — the same trap that once read a data outage aloud
    // as a confident zero.
    if (!sym || typeof r?.score === "boolean" || !Number.isFinite(score)) continue;

    const labels = convergenceLabels(r?.signals_fired);
    const rounded = Math.round(score);

    // ⚠ Digits are left as digits deliberately. kokoro-js phonemizes through
    // phonemizer (eSpeak NG), which expands numerals in its own normalisation
    // pass — "74" is already spoken "seventy-four". Spelling it out here would
    // duplicate work the engine does and fight its stress model.
    let sentence;
    if (!labels.length) {
      // No readable triggers — the score alone is still worth saying, and it is
      // honest about the fact that nothing further is known.
      sentence = `${sym}. Convergence score ${rounded}.`;
    } else if (labels.length === 1) {
      sentence = `${sym} showing ${labels[0]}. Convergence score ${rounded}.`;
    } else {
      sentence = `${sym} converging on ${spokenList(labels)}. Score ${rounded}.`;
    }

    if (rounded >= CONVERGENCE_HIGH) sentence += " High conviction.";
    else if (rounded >= CONVERGENCE_MODERATE) sentence += " Moderate conviction.";

    out.push({ text: sentence, section: "convergence" });
    spoken += 1;
  }

  // ⚠ Nothing survived the per-row guards — drop the opening line too rather
  // than announce a count and then say nothing about any of it.
  if (!spoken) return [];

  // ⚠ Gated on the TOTAL, not on how many were read: the pointer exists because
  // there is more on the board than the squawk covers.
  if (n >= CONVERGENCE_CLOSER_MIN) {
    out.push({
      text: "Full signal board available on the convergence tab.",
      section: "convergence",
    });
  }
  return out;
}

// ── SECTION 5 — autocallable barriers ─────────────────────────────────────────
// Regime transitions from /api/altdata/autocallable/alerts, newest first.
//
// ⚠⚠ HIGH AND CRITICAL ONLY. MEDIUM means a name entered the NEAR band — real,
// worth a chart label, and not worth interrupting a spoken readout for. Reading
// every MEDIUM would fill the squawk with names drifting toward levels they will
// probably not reach, and the two severities that matter would be lost in it.
// LOW is a transition between two calm regimes and is not spoken at all.
//
// ⚠⚠ THE TWO HIGH SCRIPTS SAY OPPOSITE THINGS AND MUST NOT BE MERGED. Entering
// the barrier zone is a warning; recovering out of a breach is the unwind of one.
// They are distinguished by prev_regime, not by the new regime alone — the same
// AT_BARRIER regime is reached both on the way down and on the way up.
//
// ⚠ Silence on empty, like every other section here — never "no barrier alerts".
async function squawkAutocallSection() {
  const data = await squawkGet(SQUAWK_AUTOCALL_PATH);
  const rows = Array.isArray(data?.alerts) ? data.alerts.filter(Boolean) : [];
  if (!rows.length) return [];

  const cutoff = Date.now() - SQUAWK_AUTOCALL_HOURS * 3600 * 1000;
  const fresh = rows.filter((a) => {
    const sev = String(a?.severity || "").toUpperCase();
    if (sev !== "HIGH" && sev !== "CRITICAL") return false;
    const t = Date.parse(a?.fired_at);
    // ⚠ An unparseable timestamp is DROPPED, not kept. The whole point of the
    // window is that these are current; a row we cannot date is not evidence that
    // it is recent.
    return Number.isFinite(t) && t >= cutoff;
  });
  if (!fresh.length) return [];

  const out = [];
  for (const a of fresh.slice(0, SQUAWK_AUTOCALL_N)) {
    const ticker = String(a?.ticker || "").trim();
    if (!ticker) continue;
    const sev = String(a.severity).toUpperCase();
    const prev = String(a?.prev_regime || "").toUpperCase();

    if (sev === "CRITICAL") {
      out.push({
        text: `Critical barrier alert. ${ticker} has breached the knock-in barrier. `
          + `Dealer gamma flip active. Momentum acceleration risk.`,
        section: "autocall",
      });
      continue;
    }
    if (prev === "BELOW_BARRIER") {
      out.push({
        text: `Barrier update. ${ticker} has recovered above the knock-in level. `
          + `Potential mean reversion setup.`,
        section: "autocall",
      });
      continue;
    }
    // Entering the barrier zone. ⚠ The sigma is spoken as a number only when we
    // actually have one — the monitor leaves it null when no options chain was
    // available, and "null sigma from the knock-in level" is worse than a
    // sentence that simply does not quote a distance.
    // ⚠⚠ typeof CHECK, NOT Number(). Number(null) is 0 and Number.isFinite(0)
    // is true, so a null sigma rendered as "0.00 sigma from the knock-in level"
    // — a confident claim that price is exactly AT the barrier, spoken aloud,
    // when the monitor had no options chain and measured nothing. An empty
    // string is the honest output.
    const sig = a?.distance_sigma;
    const dist = typeof sig === "number" && Number.isFinite(sig)
      ? ` ${Math.abs(Number(sig)).toFixed(2)} sigma from the knock-in level.`
      : "";
    out.push({
      text: `Barrier alert. ${ticker} entering the barrier zone.${dist}`,
      section: "autocall",
    });
  }
  return out;
}

// ── SECTION 6 — flights ────────────────────────────────────────────────────────────────────────────────────────────────────────────
// Geo-fence flight events from /api/altdata/aircraft/events, which merges
// aircraft:arrivals and aircraft:departures and returns them newest-first.
//
// ⚠⚠ THREE EVENT KINDS, NOT TWO, AND THE THIRD IS THE ONE THAT BITES. The
// backend emits "arrival", "departure" AND "departure_inferred". The previous
// formatter tested `event === "departure"` and let everything else fall through
// to the arrival branch — so an inferred departure would have been SPOKEN AS AN
// ARRIVAL at the field it had just left. Latent only because the fetch was a
// stub; it became real the moment this was wired. Each kind now selects its own
// script explicitly and nothing falls through.
//
// ⚠⚠ AN INFERRED DEPARTURE CARRIES A DETECTION TIME, NOT A WHEELS-UP TIME — the
// aircraft climbed out somewhere in the preceding poll interval and the exact
// moment is unknown. Its script therefore speaks NO clock time and says so
// aloud, rather than reading out a number that is wrong by up to ten minutes.
//
// ⚠ faa_owner / faa_make are OPTIONAL. Every event emitted before 2026-08-25
// carries faa_owner "" and no faa_make at all, because enrichment used to run
// after these were emitted. Both clauses omit on null / undefined / empty —
// speaking "registered to ." or "undefined" is worse than saying less.
// ⚠ Event timestamps are epoch SECONDS (int(time.time()) server-side); JS Date
// takes MILLISECONDS. The x1000 is the whole difference between "at 14:32" and
// "at 00:00" on 1 January 1970 — the same units trap the Finnhub feed hit.
function squawkFlightTime(ts) {
  // ⚠⚠ typeof, NOT Number(). Number(null) and Number("") are BOTH 0, and 0 is
  // finite — so a coercion-only guard turns a missing timestamp into a confident
  // "19:00 Eastern" (epoch 0 in ET). Exactly the Number(null) === 0 trap that
  // had the Bull/Bear squawk read a data outage aloud as a contrarian BUY.
  // A numeric STRING is rejected too, deliberately: the endpoint sends a JSON
  // number, so a string means the shape changed and should be visible.
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "";
  const n = ts;
  try {
    // ⚠ Reuses squawkEtParts rather than building a second Intl.DateTimeFormat.
    // ONE ET clock in this file, one place for a timezone to ever be wrong.
    const p = squawkEtParts(new Date(n * 1000));
    return `${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
  } catch { return ""; }
}

function squawkFlightsSection(events) {
  const rows = Array.isArray(events) ? events.filter(Boolean) : [];
  const out = [];
  for (const e of rows.slice(0, SQUAWK_FLIGHTS_N)) {
    const id = String(e?.callsign || e?.icao24 || "").trim();
    const airport = String(e?.airport || "").trim();
    if (!id || !airport) continue;

    const kind = String(e?.event || "").trim().toLowerCase();
    const owner = String(e?.faa_owner || "").trim();
    const make = String(e?.faa_make || "").trim();
    const at = squawkFlightTime(e?.timestamp);

    let text;
    if (kind === "departure_inferred") {
      // ⚠ No clock time, deliberately — see the note above.
      text = `Flight activity. Bizjet ${id} departed ${airport}. Exact time uncertain.`;
    } else if (kind === "departure") {
      text = `Flight departure. Bizjet ${id}, departed ${airport}`
        + `${at ? ` at ${at}` : ""} Eastern.`;
    } else {
      // Arrival, and any unrecognised kind: the endpoint stamps event_type from
      // the source LIST, so an unknown `event` string still sits on this side.
      const lead = [`Bizjet ${id}`];
      if (make) lead.push(make);
      text = `Flight arrival. ${lead.join(", ")}, arrived ${airport}`
        + `${at ? ` at ${at}` : ""} Eastern.`;
    }
    if (owner) text += ` Registered to ${owner}.`;
    out.push({ text, section: "flights" });
  }
  if (!out.length) return [];
  // ⚠ "Flight events." not "Flight activity." — the inferred-departure script
  // opens with "Flight activity", and an identical intro immediately before it
  // reads as a stutter when spoken.
  return [{ text: "Flight events.", section: "flights" }, ...out];
}

async function squawkFlightEvents() {
  const data = await squawkGet(SQUAWK_FLIGHTS_PATH);
  // ⚠ The endpoint returns an OBJECT ({events, arrivals_count, ...}), not a bare
  // array. Handing the object straight to the formatter would make Array.isArray
  // false and silence the section against perfectly good data.
  const rows = Array.isArray(data?.events) ? data.events : [];
  // ⚠⚠ BOTH SIDES IN SECONDS. Date.now() is milliseconds and the event timestamp
  // is epoch seconds; comparing them raw makes every event look ~55,000 years old
  // and silences the section permanently — a failure that reads as "no flights".
  const nowSec = Date.now() / 1000;
  return rows.filter((e) => {
    const ts = Number(e?.timestamp);
    return Number.isFinite(ts) && (nowSec - ts) <= SQUAWK_FLIGHT_MAX_AGE_S;
  });
}

// ── SECTION 6 — news headlines ────────────────────────────────────────────────
// ⚠ Reads the batch ALREADY IN STATE. No fetch: the pool is refilled on its own
// 5-minute loop and re-fetching here would double the request and let the spoken
// headlines disagree with the ones scrolling on screen.
function squawkNewsSection(headlines) {
  const rows = Array.isArray(headlines) ? headlines.filter(Boolean) : [];
  const out = [];
  for (const h of rows.slice(0, SQUAWK_NEWS_N)) {
    const line = String(h?.headline || "").trim();
    if (!line) continue;
    // ⚠ Spoken "Source. Headline." — the em dash the ticker renders is not
    // pronounced, so without a sentence break the two would run together.
    const src = String(h?.source || "").trim();
    out.push({ text: src ? `${src}. ${line}.` : `${line}.`, section: "news" });
  }
  return out;
}

// Assemble the whole readout. Every section is independent and every one is
// allowed to be empty; the result is simply shorter.
// ⚠ allSettled, not all: one dead endpoint must cost its own section and nothing
// more. A rejected section degrades to [] like an empty one.
export async function buildSquawkScript(headlines, stressState) {
  const settled = await Promise.allSettled([
    squawkMarketSection(),
    // ⚠ Between the market overview and the dark pool, deliberately: the stress
    // level is a read ON the market numbers just spoken, so it belongs directly
    // after them and before the flow detail that might corroborate it.
    squawkStressSection(stressState),
    squawkDarkpoolSection(),
    squawkOptionsSection(),
    squawkConvergenceSection(),
    // ⚠ After convergence and before flights: a barrier break is a read ON the
    // names just spoken about, so it belongs beside them rather than after the
    // aviation and news blocks that have nothing to do with the tape.
    squawkAutocallSection(),
    squawkFlightEvents(),
  ]);
  const val = (i) => (settled[i].status === "fulfilled" && Array.isArray(settled[i].value)
    ? settled[i].value
    : []);
  return [
    ...val(0),
    ...val(1),
    ...val(2),
    ...val(3),
    ...val(4),
    ...val(5),
    // ⚠ Flights moved from index 5 to 6 when the barrier section was inserted
    // above. Reading val(5) here would have spoken barrier ALERT OBJECTS through
    // the flight formatter, which reads callsign/airport and would have silently
    // dropped every one of them.
    ...squawkFlightsSection(val(6)),
    ...squawkNewsSection(headlines),
  ];
}

// ─── CHYRON ───────────────────────────────────────────────────────────────────
export function Chyron() {
  const [headlines,      setHeadlines]      = useState([]);
  const [squawkEnabled,  setSquawkEnabled]  = useState(loadSquawkEnabled);
  // ⚠ First-load only. Once the model is cached by the browser the subsequent
  // from_pretrained resolves fast enough that flashing an indicator would be
  // noise, and kokoroLoading() is false the moment _kokoro is set either way.
  const [voiceInit,      setVoiceInit]      = useState(false);
  const [squawkVolume,   setSquawkVolume]   = useState(loadSquawkVolume);
  const [keywords,       setKeywords]       = useState(loadKeywords);
  const [isHovered,      setIsHovered]      = useState(false);

  // Session-only: deliberately NOT persisted. Across a reload every headline is
  // legitimately new again, and a localStorage seen-set would grow unbounded.
  const seenIdsRef = useRef(new Set());

  // ⚠ FIRST FETCH PRIMES THE SEEN SET WITHOUT SPEAKING. On a cold mount every
  // headline is "new", so a literal read-everything-unseen rule would announce
  // ~15-30 stories back to back the moment the board loads — and again on every
  // return to this tab. A squawk box announces what BREAKS while you are
  // listening. Flip PRIME_ON_FIRST_LOAD to false to restore the literal
  // behaviour; nothing else changes.
  const PRIME_ON_FIRST_LOAD = true;
  const primedRef = useRef(false);

  // Refs mirror the live settings so the voice pump and the fetch loop read
  // current values instead of the values captured when they were created.
  const volumeRef    = useRef(squawkVolume);
  const enabledRef   = useRef(squawkEnabled);
  const headlinesRef = useRef([]);
  const keywordsRef  = useRef(keywords);
  useEffect(() => { volumeRef.current    = squawkVolume;  }, [squawkVolume]);
  useEffect(() => { enabledRef.current   = squawkEnabled; }, [squawkEnabled]);
  useEffect(() => { headlinesRef.current = headlines;     }, [headlines]);
  useEffect(() => { keywordsRef.current  = keywords;      }, [keywords]);

  // ── Carousel ──
  // The pool is the full backend payload (up to 50). The ticker shows one batch
  // of 15 at a time and advances every 360s, so the whole pool is seen over four
  // passes (~24 min) instead of only ever showing the newest 15.
  const poolRef          = useRef([]);
  const poolIndexRef     = useRef(0);
  const carouselTimerRef = useRef(null);

  // ── Squawk readout ──
  // ⚠ A SEPARATE TIMER FROM THE CAROUSEL, on its own cadence. The carousel is a
  // fixed 360s display rotation; the readout re-derives its interval from the ET
  // clock on every cycle. Sharing one timer would tie the spoken market update to
  // the scroll position, which is not what either one means.
  const squawkTimerRef     = useRef(null);
  // Set by toggleSquawk so an explicit switch-on reads immediately, while a mount
  // waits a full interval — landing on the board must not start talking at you.
  const squawkImmediateRef = useRef(false);
  // A cycle makes six network calls and can outlive its own tick on a slow link;
  // this stops two overlapping runs from queueing the readout twice.
  const squawkRunningRef   = useRef(false);
  // ⚠ SESSION-ONLY, like seenIdsRef. Persisting the last spoken stress level
  // would mean a reload during an active level-2 never announces it, which is
  // the opposite of what someone returning to the board needs to hear.
  const stressStateRef     = useRef({ lastLevel: null, lastMidday: null });

  // ── User-gesture tracking (see the autoplay note at the top of this file) ──
  // ⚠ Seeded from navigator.userActivation.hasBeenActive: the gesture that
  // unlocks this page is almost always the acknowledgement modal, which is
  // clicked BEFORE the Convergence Board — and therefore before this component
  // mounts and could hear it. Without the seed we would wait for a second,
  // redundant gesture that the browser does not require. Chromium-only; where
  // it is absent (Firefox, older Safari) this starts false and the document
  // listener below picks up the next real gesture.
  const hasGestureRef = useRef(
    typeof navigator !== "undefined" &&
    navigator.userActivation != null &&
    navigator.userActivation.hasBeenActive === true
  );

  // ── Voice queue ──
  const voiceQueueRef = useRef([]);
  const speakingRef   = useRef(false);
  // Generation counter — bumped on every cancel. ⚠ speechSynthesis.cancel()
  // fires `onend` on the utterance it kills in most engines, so without this
  // guard a cancelled queue would resume itself from that very callback.
  const voiceGenRef   = useRef(0);
  // ⚠ The utterance is held on a ref, not a local const. Chrome garbage-collects
  // a mid-flight SpeechSynthesisUtterance that nothing references — taking its
  // pending onend/onerror with it, which latches speakingRef true forever and
  // freezes the queue permanently. Retaining it is the documented mitigation.
  const utteranceRef  = useRef(null);
  // ⚠ The Kokoro counterpart of utteranceRef. An <audio> element left playing
  // is exactly the ghost voice the visibility handler exists to prevent, and
  // speechSynthesis.cancel() does not touch it — it has to be stopped by hand.
  const kokoroAudioRef = useRef(null);
  // ⚠⚠ THE KOKORO MUTEX. speakingRef is the queue's latch, but the STALL
  // WATCHDOG clears it deliberately — that is its whole job — and for Kokoro the
  // watchdog is armed on `play` and therefore runs DURING playback. Its budget
  // is derived from VOICE_RATE, which Kokoro ignores entirely, so any clip that
  // runs longer than the estimate had the watchdog drop the latch mid-audio and
  // pump the next line straight over the top of it. Each new clip then armed its
  // own watchdog, so it compounded. This flag is held for the whole
  // synthesis-plus-playback span and is the thing that actually serialises the
  // Kokoro path.
  const kokoroPlayingRef = useRef(false);
  const stallTimerRef = useRef(null);

  // ⚠ Used by BOTH stopVoice and the visibility handler. Pausing is not enough:
  // the element must also be detached from its handlers, or `finish` fires on
  // the paused element and advances a queue that was just cancelled.
  const stopKokoroAudio = useCallback(() => {
    // ⚠ Released here as well as in advanceOnce. A stop cancels the in-flight
    // advanceOnce via the generation guard, so without this a mute or a tab-hide
    // during playback would leave the mutex latched and the queue dead on
    // resume.
    kokoroPlayingRef.current = false;
    const el = kokoroAudioRef.current;
    kokoroAudioRef.current = null;
    if (!el) return;
    try {
      el.onended = null; el.onerror = null; el.onplay = null;
      el.pause();
      if (el._objectUrl) URL.revokeObjectURL(el._objectUrl);
    } catch { /* element already torn down */ }
  }, []);

  const stopVoice = useCallback(() => {
    voiceGenRef.current += 1;
    voiceQueueRef.current = [];
    speakingRef.current = false;
    // ⚠ The stall timer must die with the queue. Left armed, it would fire after
    // an unmount or a mute and pump a cancelled queue back to life.
    clearTimeout(stallTimerRef.current);
    stallTimerRef.current = null;
    utteranceRef.current = null;
    stopKokoroAudio();
    try { window.speechSynthesis?.cancel(); } catch { /* unsupported */ }
  }, [stopKokoroAudio]);

  // ⚠ Cap the queue. While the latch is held nothing drains, but the 5-minute
  // fetch loop keeps pushing — so a stall used to grow the backlog without
  // bound, and a recovery would then read the whole accumulation at the user.
  const enqueueVoice = useCallback((items) => {
    if (!items || !items.length) return;
    if (voiceQueueRef.current.length >= VOICE_QUEUE_CAP) return;
    voiceQueueRef.current.push(...items);
  }, []);

  const pumpVoice = useCallback(() => {
    const synth = window.speechSynthesis;
    if (!synth || speakingRef.current) return;
    // ⚠⚠ ABOVE THE SHIFT, DELIBERATELY. Below it this would consume a queue item
    // and drop it on the floor — losing a line of the readout every time the
    // watchdog fired. Returning here leaves the queue intact; the audio's
    // onended fires shortly after and advance() resumes it normally.
    if (kokoroPlayingRef.current) return;
    // ⚠ Gate BEFORE the shift, so a queue built while the document was still
    // locked survives intact and drains the moment a gesture lands rather than
    // being consumed into silence. In practice nothing is enqueued while
    // blocked (the fetch loop checks too) — this is the belt to that braces.
    if (!hasGestureRef.current) return;
    const next = voiceQueueRef.current.shift();
    if (!next) return;

    // ── Mobile chunking ──
    // ⚠⚠ THIS RUNS BETWEEN THE SHIFT AND THE LATCH, AND THAT PLACEMENT IS THE
    // WHOLE TRICK. speakingRef is still false here, so the re-entrant pumpVoice()
    // below proceeds normally; doing this a few lines further down — after
    // `speakingRef.current = true` — would make the recursive call return
    // instantly at the guard at the top of this function and nothing would ever
    // be spoken. Depth is exactly 1: a chunk is <= VOICE_CHUNK_MAX by
    // construction, so it re-chunks to itself and takes the single-chunk path.
    // `text` overrides the headline form — used for the spoken confirmation,
    // which is not a headline and must not be read as "Squawk box. active.".
    const fullText = next.text || `${next.source}. ${next.headline}`;
    const chunks = _chunkText(fullText);
    // Nothing sayable. The item is already shifted off, so the queue is one
    // shorter and this recursion always makes progress.
    if (!chunks.length) { pumpVoice(); return; }
    if (chunks.length > 1) {
      // ⚠⚠ unshift, NOT enqueueVoice — THE CAP IS DELIBERATELY BYPASSED HERE.
      // enqueueVoice refuses the WHOLE push at VOICE_QUEUE_CAP, and these chunks
      // are the remainder of an utterance that is about to start speaking:
      // dropping them would truncate a readout mid-sentence, which is the worst
      // possible place to lose audio and exactly what chunking exists to avoid.
      // The cap governs what ENTERS the queue, not how an item already accepted
      // is subdivided.
      voiceQueueRef.current.unshift(...chunks.map((c, i) => ({
        text: c,
        source: next.source,
        headline: next.headline,
        isChunk: true,
        isLastChunk: i === chunks.length - 1,
      })));
      pumpVoice();
      return;
    }

    const gen = voiceGenRef.current;
    speakingRef.current = true;
    try {
      const text = chunks[0];

      // ── Stall watchdog, shared by both engines ──
      // Extracted so the Kokoro path can arm it at a DIFFERENT moment, and now
      // with a DIFFERENT budget.
      //
      // ⚠ `budgetMs` is optional. Omitted, it falls back to the text-length
      // estimate — which is derived from VOICE_RATE and therefore describes Web
      // Speech, the only engine whose speed that rate actually governs. Web
      // Speech has no audio element and no knowable duration, so an estimate is
      // the best available bound there and calling armStall() bare is unchanged
      // behaviour for it.
      // ⚠⚠ Kokoro passes a MEASURED duration instead. The estimate was the
      // direct cause of the box talking over itself: a neural clip that ran
      // longer than a 400ms-per-word guess had the watchdog fire mid-playback.
      // Safe to call repeatedly — the clearTimeout below makes re-arming a
      // replacement, which is what the arm-then-upgrade sequence relies on.
      const armStall = (budgetMs) => {
        const words = text.trim().split(/\s+/).filter(Boolean).length || 1;
        const stallBudget = Number.isFinite(budgetMs) && budgetMs > 0
          ? budgetMs
          : Math.max(
              text.length * STALL_MS_PER_CHAR,
              words * STALL_MS_PER_WORD,
            ) + STALL_BUFFER_MS;
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = setTimeout(() => {
          stallTimerRef.current = null;
          speakingRef.current = false;
          if (gen !== voiceGenRef.current) return;
          pumpVoice();
        }, stallBudget);
      };

      // ⚠ Hoisted ABOVE the engine branch so both paths share one definition.
      // It was previously declared alongside the utterance; the behaviour is
      // byte-identical, only the position moved.
      const advance = () => {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
        speakingRef.current = false;
        if (gen !== voiceGenRef.current) return;   // cancelled mid-flight
        setTimeout(() => {
          if (gen === voiceGenRef.current) pumpVoice();
        }, VOICE_GAP_MS);
      };

      // ── Engine selection ──
      // ⚠⚠ THE ONLY THING THAT CHANGES IS THE ENGINE. Everything around it —
      // the queue, the chunking, the 900ms gap, the generation guard, the
      // gesture gate, the visibility handling — is identical for both paths.
      if (kokoroReady()) {
        // ⚠⚠ CLEARED IN advanceOnce, NOT IN THE AUDIO HANDLERS. There are FOUR
        // ways out of this branch and two of them never create an audio element
        // at all: a cancel during synthesis, and generate() itself throwing.
        // Clearing only in onended/onerror would leave the flag stuck true on
        // either of those and lock the queue permanently — the exact silence the
        // watchdog exists to prevent. All four exits route through advanceOnce.
        kokoroPlayingRef.current = true;
        const advanceOnce = (() => {
          let done = false;
          return () => {
            if (done) return;
            done = true;
            kokoroPlayingRef.current = false;
            advance();
          };
        })();
        kokoroSynthesize(text, loadKokoroVoice())
          .then((el) => {
            // ⚠ Cancelled DURING synthesis — a squawk mute or a tab hide while
            // the model was working. Drop the audio on the floor; playing it
            // now would speak into a room that asked for silence.
            if (!el || gen !== voiceGenRef.current) {
              if (el?._objectUrl) URL.revokeObjectURL(el._objectUrl);
              advanceOnce();
              return;
            }
            kokoroAudioRef.current = el;
            el.volume = Math.min(1, Math.max(0, volumeRef.current / 100));
            const finish = () => {
              try { URL.revokeObjectURL(el._objectUrl); } catch { /* ignore */ }
              if (kokoroAudioRef.current === el) kokoroAudioRef.current = null;
              advanceOnce();
            };
            el.onended = finish;
            el.onerror = finish;

            // ── Watchdog: ARM, THEN UPGRADE ──
            // ⚠⚠ THE CLIP IS NEVER LEFT UNGUARDED. Waiting for loadedmetadata
            // before arming at all would mean a clip whose metadata never
            // resolves has no watchdog and nothing to trigger a fallback — the
            // absence of an event cannot itself be observed. So `play` always
            // arms something, and the measured duration replaces it the moment
            // it is known.
            // ⚠ duration is NaN on a fresh element and can be NaN or Infinity
            // on a malformed blob, so it is range-checked rather than truthiness
            // -checked: `Infinity * 1000 + 2000` is a timer that never fires.
            const armFromDuration = () => {
              const d = el.duration;
              if (!Number.isFinite(d) || d <= 0) return false;
              armStall(d * 1000 + KOKORO_STALL_BUFFER_MS);
              return true;
            };
            el.onloadedmetadata = armFromDuration;
            // ⚠ Bare armStall() here is the TEXT-ESTIMATE FALLBACK, reached only
            // when duration is still unknown at play time.
            el.onplay = () => { if (!armFromDuration()) armStall(); };
            // ⚠⚠ blob: URLs can reach HAVE_METADATA before these handlers are
            // attached, in which case loadedmetadata has ALREADY fired and will
            // never fire again — a handler-only approach would silently stay on
            // the estimate forever. Read readyState directly to cover it.
            if (el.readyState >= 1) armFromDuration();

            el.play().catch(finish);
          })
          .catch((err) => {
            // ⚠ One bad synthesis must not silence the box. Log, advance, and
            // let the next line try again; a hard engine failure has already
            // set _kokoroFailed inside initKokoro.
            try { console.warn("[squawk] Kokoro synthesis failed:", err); } catch { /* ignore */ }
            advanceOnce();
          });
        return;
      }

      // ⚠ Not ready — speak THIS line through Web Speech and start the model
      // load in the background. Fire-and-forget: the download must never delay
      // the current utterance. Later lines pick Kokoro up automatically once
      // kokoroReady() flips.
      initKokoro();

      utteranceRef.current = new SpeechSynthesisUtterance(text);
      const u = utteranceRef.current;
      // ⚠ Resolved per utterance rather than cached. getVoices() is empty on the
      // first call in Chrome and populates asynchronously, so a list captured at
      // mount would strand the user on the browser default for the session; and
      // reading storage here means a change in Settings takes effect on the very
      // next headline with no event plumbing.
      // ⚠ A name saved on another device may not exist here — leaving u.voice
      // unset is the correct fallback, and the browser picks its own default.
      // ⚠⚠ RESOLVING AT SPEAK TIME IS ALSO WHAT MAKES THIS SAFE ON iOS, where
      // Safari does not reliably fire `voiceschanged` at all, and on Chrome,
      // where getVoices() is empty on the first call and fills in asynchronously.
      // Neither matters here: by the time an utterance is actually spoken the
      // list has populated, so there is nothing to wait for and no event to
      // miss. A voicesRef primed on mount would have needed both the event AND a
      // synchronous fallback to cover the two platforms — this needs neither.
      // The list-building that DOES need that treatment is the Settings dropdown
      // (src/SettingsPanel.jsx), which must render the voices before any
      // utterance exists; it carries the event, a direct read and a settle timer.
      const wantVoice = loadSquawkVoice();
      if (wantVoice) {
        const match = (synth.getVoices() || []).find((v) => v && v.name === wantVoice);
        if (match) u.voice = match;
      }
      u.rate   = VOICE_RATE;
      u.pitch  = VOICE_PITCH;
      // ⚠⚠ utterance.volume IS IGNORED ON iOS — system volume controls speech
      // output on that platform, so the squawk volume slider has no effect
      // there. It is honoured on Android and desktop. Still set unconditionally:
      // it is correct everywhere else, and iOS ignoring a property is not a
      // reason to stop sending it. ⚠ Note this also means a user who has muted
      // the slider on iOS will still hear the box — the mute that works on every
      // platform is the SQUAWK toggle, which stops the queue rather than the
      // volume.
      u.volume = Math.min(1, Math.max(0, volumeRef.current / 100));

      u.onend   = advance;
      u.onerror = advance;
      // ⚠⚠ UNWEDGE A PAUSED ENGINE BEFORE SPEAKING. A speak() issued while the
      // engine is paused is accepted and QUEUED but never plays, so one stray
      // pause anywhere silences every subsequent utterance with no error and no
      // onend — the queue drains into nothing. The desktop keepalive above can
      // leave the engine paused if its resume() races a state change, and any
      // platform can arrive here paused after an interruption. Cheap to check,
      // and it is the difference between one lost utterance and a dead queue.
      try { if (synth.paused) synth.resume(); } catch { /* not supported */ }
      synth.speak(u);

      // ── Stall watchdog ──
      // The queue advances ONLY from onend/onerror, so an utterance that never
      // fires either one leaves speakingRef latched true and every later
      // pumpVoice() returns at the guard above — silence for the life of the
      // component. This releases the latch and moves on. Generation-guarded, so
      // it can never resurrect a queue that was cancelled while it was armed.
      // ⚠ Budgeted from BOTH measures, whichever is larger — see the constants.
      // Characters govern long, number-dense squawk sections; words govern short
      // headlines, where the per-word figure is the more generous of the two.
      armStall();
    } catch {
      speakingRef.current = false;
    }
  }, []);

  // ── Chrome keepalive — DESKTOP ONLY ──
  // Desktop Chrome silently stalls speechSynthesis after roughly 15 seconds of
  // continuous speech and never fires onend for that utterance. A periodic
  // pause()/resume() while it is speaking keeps the engine alive past that
  // ceiling. Guarded on `speaking`: pausing an idle engine can leave it paused.
  //
  // ⚠⚠ NEVER RUN THIS ON MOBILE — IT IS THE BUG IT LOOKS LIKE A FIX FOR.
  // Android Chrome delegates to the platform TTS service, where pause() is not
  // reliably implemented: it stops speech, and the resume() on the very next line
  // frequently does NOT restart it. The engine is then wedged in a paused state
  // that reports `speaking === true` while producing no audio and never firing
  // onend. Because this interval free-runs every 10s against a readout that
  // speaks for 80-120s, it lands MID-UTTERANCE eight to twelve times per run.
  // That is both halves of the reported mobile symptom from one line: the
  // utterance is cut mid-WORD wherever the tick falls, and nothing re-fires
  // afterwards because every later speak() queues behind a paused engine.
  // ⚠ This is also why the 180-character chunking did NOT fix it — measured, the
  // readout's utterances run 19-118 characters and never reach the threshold, so
  // chunking never fired. It remains correct as a guard against a long headline;
  // it was simply never the cause.
  // ⚠ Mobile does not need the keepalive: the ~15s stall is a desktop-Chrome
  // defect, not a platform-TTS one. iOS is excluded for the same reason, and its
  // own failure mode (suspension while backgrounded) is handled by the
  // visibilitychange handler below instead.
  useEffect(() => {
    if (isMobile) return undefined;
    const synth = window.speechSynthesis;
    if (!synth) return undefined;
    const id = setInterval(() => {
      try {
        if (synth.speaking) { synth.pause(); synth.resume(); }
      } catch { /* engine unavailable — nothing to keep alive */ }
    }, KEEPALIVE_MS);
    return () => clearInterval(id);
  }, []);

  // ⚠ NON-NEGOTIABLE: cancel on unmount. Without it the queue keeps reading
  // after the user navigates to another tab — ghost voices with no visible
  // source and no control on screen to stop them.
  useEffect(() => stopVoice, [stopVoice]);

  // ── Background suspension ──
  // iOS suspends speechSynthesis when the tab is backgrounded or the screen
  // locks. Cancel cleanly on hide rather than deadlock. Resume queue on return
  // to foreground.
  //
  // ⚠⚠ THE DEADLOCK IS THE POINT. On iOS a suspended engine leaves
  // speechSynthesis.speaking TRUE while producing no audio and never firing
  // onend — so speakingRef stays latched, every later pumpVoice() returns at the
  // guard, and the box is silent for the life of the component. The stall
  // watchdog would eventually break it, but only after the full budget of an
  // utterance that never actually started. Cancelling on hide is both faster and
  // honest about what happened.
  const handleVisibilityChange = useCallback(() => {
    if (typeof document === "undefined") return;
    if (document.hidden) {
      // ⚠⚠ BUMP THE GENERATION BEFORE CANCELLING. cancel() fires onend on the
      // utterance it kills in most engines, so `advance` runs — and with the
      // generation unchanged it would schedule another pumpVoice() while the tab
      // is still hidden. That is precisely the resurrection voiceGenRef exists
      // to prevent.
      // ⚠⚠ THE QUEUE IS DELIBERATELY PRESERVED — this is NOT stopVoice().
      // Backgrounding a tab is a request to PAUSE the readout, not to discard
      // it; stopVoice() empties voiceQueueRef and would leave nothing to resume.
      voiceGenRef.current += 1;
      speakingRef.current = false;
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
      utteranceRef.current = null;
      stopKokoroAudio();
      try { window.speechSynthesis?.cancel(); } catch { /* unsupported */ }
      return;
    }
    if (!enabledRef.current) return;
    if (!voiceQueueRef.current.length) return;
    // ⚠ Generation-guarded so a hide → show → hide sequence cannot stack resume
    // timers: the second hide bumps the generation and every pending resume from
    // before it becomes a no-op instead of pumping a queue that was re-cancelled.
    // ⚠ enabledRef re-checked inside the timer as well — the user can mute
    // during the delay.
    const gen = voiceGenRef.current;
    setTimeout(() => {
      if (gen !== voiceGenRef.current) return;
      if (!enabledRef.current) return;
      pumpVoice();
    }, VISIBILITY_RESUME_MS);
  }, [pumpVoice]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [handleVisibilityChange]);

  // ── First gesture unlocks audio, and catches the user up ──
  // Idempotent: only the false→true transition does anything.
  const markGesture = useCallback(() => {
    if (hasGestureRef.current) return;
    hasGestureRef.current = true;

    // Everything queued while blocked was dropped rather than accumulated, so
    // read back the top of the rotation — keyword hits first, else newest — to
    // give immediate audible proof the box is live. Capped at 3: the point is
    // feedback, not draining a 16-item backlog at the user.
    // ⚠⚠ DELIBERATELY DOES NOT TOUCH seenIdsRef. Those headlines stay marked as
    // seen; clearing the set would make every one of them "new" again on the
    // next refresh and announce the whole rotation a second time.
    if (!enabledRef.current) return;
    const recent = headlinesRef.current.slice(0, GESTURE_REQUEUE);
    if (!recent.length) return;
    enqueueVoice(recent);
    pumpVoice();
  }, [enqueueVoice, pumpVoice]);

  // Document-level, because the autoplay gate is document-level. Capture phase
  // so a handler that stops propagation cannot hide the gesture from us.
  useEffect(() => {
    if (hasGestureRef.current) return undefined;
    const onGesture = () => markGesture();
    const opts = { capture: true };
    document.addEventListener("pointerdown", onGesture, opts);
    document.addEventListener("keydown", onGesture, opts);
    return () => {
      document.removeEventListener("pointerdown", onGesture, opts);
      document.removeEventListener("keydown", onGesture, opts);
    };
  }, [markGesture]);

  // ── Settings sync (same tab via our event, other tabs via `storage`) ──
  useEffect(() => {
    const resync = () => {
      setSquawkEnabled(loadSquawkEnabled());
      setSquawkVolume(loadSquawkVolume());
      setKeywords(loadKeywords());
      // ⚠ Rides the SAME event rather than adding a second channel. initKokoro
      // dispatches it on load start, success and failure, so the indicator
      // appears and clears without any polling.
      setVoiceInit(kokoroLoading());
    };
    window.addEventListener(SETTINGS_EVENT, resync);
    window.addEventListener("storage", resync);
    return () => {
      window.removeEventListener(SETTINGS_EVENT, resync);
      window.removeEventListener("storage", resync);
    };
  }, []);

  // Turning the squawk box off must silence what is already queued, not just
  // stop new items being added.
  useEffect(() => {
    if (!squawkEnabled) stopVoice();
  }, [squawkEnabled, stopVoice]);

  // ── Fetch → pool, and the carousel that serves it ──
  const keywordKey = keywords.join("|").toLowerCase();

  // Display = every keyword hit in the WHOLE POOL, then the current batch.
  // ⚠⚠ KEYWORD HITS ARE GLOBAL, NOT BATCH-SCOPED. A hit sitting in batch 4 of a
  // 50-item pool would otherwise wait up to ~24 minutes to reach the screen,
  // which defeats the entire point of an alert keyword.
  // ⚠ Matched at DISPLAY time, not at ingest, so editing keywords re-marks the
  // existing pool instantly instead of waiting for the next fetch.
  const buildDisplay = useCallback(() => {
    const kws = keywordsRef.current;
    const marked = poolRef.current.map((h) => ({
      ...h,
      isKeywordMatch: matchesKeywords(h.headline, h.summary, kws),
    }));
    const hits = marked.filter((h) => h.isKeywordMatch);
    const hitIds = new Set(hits.map((h) => h.id));
    const batch = sliceWrap(marked, poolIndexRef.current, CAROUSEL_BATCH)
      .filter((h) => !hitIds.has(h.id));
    return [...hits, ...batch];
  }, []);

  // Speak whatever in `display` has not been seen this session. Shared by the
  // carousel advance and the initial load, which suppresses it.
  const announce = useCallback((display) => {
    const fresh = display.filter((h) => !seenIdsRef.current.has(h.id));
    display.forEach((h) => seenIdsRef.current.add(h.id));
    if (!fresh.length) return;
    // ⚠ The chime is gated on the squawk toggle as well as on a keyword hit.
    // The toggle reads as "audio off"; a box that still pings after being muted
    // would be indistinguishable from a bug.
    if (!enabledRef.current) return;
    // ⚠ Nothing is created or enqueued while the document is still locked — an
    // AudioContext built here would be born suspended and a speak() call would
    // be dropped on the floor. markGesture's re-queue covers the gap once a
    // gesture lands, which is why this DROPS rather than accumulating.
    if (!hasGestureRef.current) return;
    const freshHits = fresh.filter((h) => h.isKeywordMatch);
    const freshRest = fresh.filter((h) => !h.isKeywordMatch);
    if (freshHits.length) playChime(volumeRef.current / 100);
    // Keyword hits read first — the chime announces them, so they should be
    // what follows it. Routed through enqueueVoice, never a direct push, so the
    // queue cap holds during a stall.
    enqueueVoice([...freshHits, ...freshRest]);
    pumpVoice();
  }, [enqueueVoice, pumpVoice]);

  // ── Carousel advance ──
  const advance = useCallback(() => {
    const pool = poolRef.current;
    if (pool.length) {
      poolIndexRef.current += CAROUSEL_BATCH;
      if (poolIndexRef.current >= pool.length) poolIndexRef.current = 0;
    }
    const display = buildDisplay();
    setHeadlines(display);
    announce(display);
  }, [buildDisplay, announce]);

  // ⚠ The timer is armed ONCE and reads the advance through a ref. Keying the
  // interval effect on `advance` itself would tear down and re-arm the 360s
  // timer on every settings change, so a user fiddling with keywords could hold
  // the carousel permanently at zero.
  const advanceRef = useRef(advance);
  useEffect(() => { advanceRef.current = advance; }, [advance]);

  useEffect(() => {
    const tick = () => {
      advanceRef.current();
      carouselTimerRef.current = setTimeout(tick, CAROUSEL_INTERVAL_MS);
    };
    carouselTimerRef.current = setTimeout(tick, CAROUSEL_INTERVAL_MS);
    return () => clearTimeout(carouselTimerRef.current);
  }, []);

  // ── Fetch → pool ──
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      let rows = [];
      try {
        const res = await backendFetch(NEWS_PATH, NEWS_PATH);
        if (!res?.ok) return;
        const data = await res.json();
        rows = Array.isArray(data) ? data : [];
      } catch { return; }
      if (cancelled) return;

      // Normalize only. No sort, no floor, no recency cut — /api/news/chyron
      // already deduped, paywall-filtered, floored and ordered this payload.
      const incoming = [];
      for (const a of rows) {
        if (!a) continue;
        const id = a.id || a.url;
        const url = a.url;
        const headline = a.title;
        if (!id || !url || !headline) continue;
        const summary = a.summary || "";
        const source = a.source || "—";
        // Chyron quality filter — see CHYRON_EXCLUDE. Applied before anything
        // enters the pool, so an excluded item is never displayed, never
        // keyword-matched and never spoken. The news desk is unaffected.
        if (!passesChyronFilter(source, headline, summary)) continue;
        incoming.push({
          id,
          headline,
          summary,
          url,
          source,
          sourceApi: a.sourceApi || "",
          publishedAt: typeof a.publishedAt === "number" ? a.publishedAt : 0,
        });
      }

      // Merge into the pool, newest-first, capped at POOL_MAX.
      // ⚠⚠ poolIndexRef IS DELIBERATELY NOT RESET. A refill mid-pass must not
      // yank the ticker back to the top of the pool; new items land at the
      // front and are reached on a later advance.
      const byId = new Map(poolRef.current.map((h) => [h.id, h]));
      for (const item of incoming) if (!byId.has(item.id)) byId.set(item.id, item);
      poolRef.current = [...byId.values()]
        .sort((x, y) => y.publishedAt - x.publishedAt)
        .slice(0, POOL_MAX);

      // ⚠⚠ THE FIRST LOAD SERVES A BATCH SILENTLY. Voice begins at the first
      // carousel advance, 360s in. Landing on the board must not trigger 15
      // headlines read back to back — and on a return visit the seen-set is
      // empty again, so without this every navigation would re-announce.
      if (!primedRef.current) {
        primedRef.current = true;
        poolIndexRef.current = 0;
        const display = buildDisplay();
        setHeadlines(display);
        if (PRIME_ON_FIRST_LOAD) {
          display.forEach((h) => seenIdsRef.current.add(h.id));
          return;
        }
        announce(display);
        return;
      }
      // Later refills only top the pool up; the screen changes on advance.
    };

    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [buildDisplay, announce]);

  // A keyword edit re-marks the pool immediately rather than leaving the user
  // to wait up to six minutes for the next advance.
  useEffect(() => {
    if (!poolRef.current.length) return;
    setHeadlines(buildDisplay());
  }, [keywordKey, buildDisplay]);

  // ── Squawk readout cycle ──
  // Builds the full script from live data and hands it to the SAME voice queue
  // the headlines use, so one pump paces both and they cannot talk over each other.
  const runSquawkCycle = useCallback(async () => {
    // ⚠⚠ GATED BEFORE THE FETCHES, NOT AFTER. A cycle that cannot be heard must
    // not cost six network round trips: the box can be muted, the document can
    // still be locked by the autoplay gate, and the market can be closed — none
    // of which pumpVoice would reveal until the work was already paid for.
    if (!enabledRef.current) return;
    if (!hasGestureRef.current) return;
    if (_isMarketHours() === "closed") return;
    if (squawkRunningRef.current) return;
    squawkRunningRef.current = true;
    try {
      const script = await buildSquawkScript(headlinesRef.current, stressStateRef.current);
      if (!script.length) return;
      // Re-checked after the await: the user can mute during the fetches, and
      // enqueueing here would speak into a box they just switched off.
      if (!enabledRef.current) return;
      // ⚠⚠ ONE ATOMIC PUSH, NOT ONE CALL PER UTTERANCE. enqueueVoice refuses the
      // WHOLE push at cap, so pushing item by item would let a readout in half
      // way and cut it off mid-sequence — precisely the symptom this feature must
      // not produce. All-or-nothing is the honest behaviour, and VOICE_QUEUE_CAP
      // is sized so it does not trigger.
      // ⚠⚠ `text` IS SET AS WELL AS `headline`. pumpVoice prefers `text` and
      // otherwise speaks `${source}. ${headline}` — so without it every line
      // would be read as "market. Market update…", announcing the section name.
      enqueueVoice(script.map((u) => ({
        text: u.text,
        headline: u.text,
        source: u.section,
        isKeywordMatch: false,
      })));
      pumpVoice();
    } catch {
      /* one failed cycle must never break the schedule */
    } finally {
      squawkRunningRef.current = false;
    }
  }, [enqueueVoice, pumpVoice]);

  // ⚠ Same reason the carousel arms its timer once and reads through a ref:
  // keying the schedule on the callback itself would tear it down and re-arm on
  // every settings change, so a user dragging the volume slider could hold the
  // readout permanently at zero.
  const squawkCycleRef = useRef(runSquawkCycle);
  useEffect(() => { squawkCycleRef.current = runSquawkCycle; }, [runSquawkCycle]);

  useEffect(() => {
    clearTimeout(squawkTimerRef.current);
    squawkTimerRef.current = null;
    // Toggling the box off clears the schedule here; the `!squawkEnabled` effect
    // above calls stopVoice(), which is what cancels speech already in flight.
    if (!squawkEnabled) { squawkImmediateRef.current = false; return undefined; }

    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      await squawkCycleRef.current();
      if (stopped) return;
      // ⚠⚠ RE-EVALUATED EVERY CYCLE, never captured once. The cadence has to
      // tighten by itself at 09:30 and loosen again at 16:00 with no remount — a
      // session left open across the open would otherwise run all day on whatever
      // interval happened to be in force when the component mounted.
      squawkTimerRef.current = setTimeout(tick, squawkIntervalMs(_isMarketHours()));
    };

    const immediate = squawkImmediateRef.current;
    squawkImmediateRef.current = false;
    squawkTimerRef.current = setTimeout(
      tick,
      immediate ? SQUAWK_IMMEDIATE_MS : squawkIntervalMs(_isMarketHours()),
    );

    // ⚠ `stopped` guards the await gap: without it a cycle already in flight when
    // the box is muted would still reschedule itself after the timer was cleared.
    return () => {
      stopped = true;
      clearTimeout(squawkTimerRef.current);
      squawkTimerRef.current = null;
    };
  }, [squawkEnabled]);

  // ── Squawk toggle (on the bar itself) ──
  // ⚠ ORDER IS LOAD-BEARING: the gesture is registered FIRST, so the tone and
  // the confirmation below are themselves running on an unlocked document.
  // ⚠ markGesture reads the PRE-toggle enabled state, which is what we want in
  // both directions: clicking to ENABLE gets the tone + confirmation instead of
  // three old headlines, and clicking to MUTE has its re-queue wiped by the
  // stopVoice() immediately after — i.e. muting stays silent.
  const toggleSquawk = useCallback(() => {
    markGesture();

    const next = !enabledRef.current;
    try { localStorage.setItem(SQUAWK_ENABLED_KEY, next ? "true" : "false"); } catch { /* ignore */ }
    setSquawkEnabled(next);

    if (!next) {
      stopVoice();
    } else {
      // ⚠ Read the moment the box is switched on, rather than waiting out a
      // 3-or-10-minute interval in silence — that click IS the request to hear
      // it. The scheduling effect below consumes and clears this flag; a mount
      // leaves it false and waits a full interval instead.
      squawkImmediateRef.current = true;
      // Enabling is a deliberate request for audio, so answer it audibly:
      // a test tone proves the AudioContext path, the spoken line proves the
      // speech path. Together they turn "is this thing on?" into one click.
      playChime(volumeRef.current / 100);
      // Held until the tone has finished so the two do not overlap. Guarded on
      // the voice generation, so an unmount or a re-mute inside that window
      // cancels the confirmation instead of speaking it into an empty room.
      const gen = voiceGenRef.current;
      setTimeout(() => {
        if (gen !== voiceGenRef.current) return;
        voiceQueueRef.current.push({ text: CONFIRM_TEXT });
        pumpVoice();
      }, CHIME_S * 1000 + 60);
    }

    publishSettingsChange();
  }, [markGesture, pumpVoice, stopVoice]);

  const durationS = Math.max(MIN_DURATION_S, headlines.length * SECONDS_PER_HEADLINE);

  // One rendered copy of the list. The track holds two of these side by side and
  // animates a full 50% — the classic marquee trick, so the loop has no gap.
  const renderRun = (copy) => (
    <div
      key={copy}
      aria-hidden={copy === 1 ? "true" : undefined}
      style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
    >
      {headlines.map((h) => (
        <span key={`${copy}-${h.id}`} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <a
            href={h.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: h.isKeywordMatch ? KEYWORD_COLOR : C.text,
              textDecoration: "none",
              fontFamily: font,
              fontSize: 12,
              whiteSpace: "nowrap",
              fontWeight: h.isKeywordMatch ? 700 : 400,
            }}
          >
            {h.isKeywordMatch ? "⚡ " : ""}{h.source} — {h.headline}
          </a>
          <span style={{ margin: "0 16px", color: C.textDim, fontSize: 12 }}>·</span>
        </span>
      ))}
    </div>
  );

  return (
    <>
      {/* Keyframes are injected here rather than in index.html — that file is
          where qsPulse lives, but it is outside this feature's scope. */}
      <style>{
        "@keyframes chyron-scroll { from { transform: translateX(0); } " +
        "to { transform: translateX(-50%); } }"
      }</style>

      {/* Any click inside the chyron counts as the unlocking gesture. The
          document listener already sees these (it is on the capture phase);
          this is the explicit, local guarantee. Idempotent after the first. */}
      <div
        onClick={markGesture}
        style={{
          display: "flex",
          alignItems: "center",
          height: 32,
          background: C.panelAlt,
          borderBottom: `1px solid ${C.border}`,
          borderRadius: 3,
          marginBottom: 10,
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Ticker */}
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ flex: 1, minWidth: 0, overflow: "hidden", position: "relative" }}
        >
          {headlines.length === 0 ? (
            <div style={{
              fontFamily: font, fontSize: 12, color: C.textDim,
              paddingLeft: 10, whiteSpace: "nowrap",
            }}>
              Loading headlines…
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                width: "max-content",
                animation: `chyron-scroll ${durationS}s linear infinite`,
                animationPlayState: isHovered ? "paused" : "running",
              }}
            >
              {renderRun(0)}
              {renderRun(1)}
            </div>
          )}
        </div>

        {/* Controls — fixed, outside the scrolling track */}
        <button
          onClick={toggleSquawk}
          title={squawkEnabled ? "Squawk box on — click to mute" : "Squawk box off — click to unmute"}
          aria-label={squawkEnabled ? "Mute squawk box" : "Unmute squawk box"}
          style={{
            display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
            height: "100%", padding: "0 10px",
            background: C.panel,
            border: "none",
            borderLeft: `1px solid ${C.border}`,
            color: squawkEnabled ? C.amber : C.textDim,
            fontFamily: font, fontSize: 11, fontWeight: 700,
            letterSpacing: "0.1em", cursor: "pointer", whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: 13 }}>{squawkEnabled ? "🔊" : "🔇"}</span>
          SQUAWK
          {/* ⚠ First model load only, and deliberately understated — the box is
              already speaking through Web Speech while this shows, so it is a
              note about an upgrade in progress, not a warning that anything is
              broken or waiting. */}
          {voiceInit && (
            <span style={{
              fontSize: 9, fontWeight: 400, color: C.textDim,
              letterSpacing: 0, marginLeft: 2,
            }}>
              voice initializing…
            </span>
          )}
        </button>
      </div>
    </>
  );
}

export default Chyron;
